<template>
  <v-container id="dashboard-manager" fluid tag="section" class="dm-wrappe" style="padding: 24px 96px;">

    <div class="dm-header mb-6">
      <div class="d-flex align-center">
        <div class="dm-header-icon mr-4">
          <v-icon color="#22d3ee" size="28">mdi-view-dashboard-outline</v-icon>
        </div>
        <div>
          <div class="dm-header-title">Dashboard</div>
          <div class="dm-header-subtitle">Visão geral de gestão de pessoas — PDIs, reuniões, competências e feedbacks do seu time.</div>
        </div>
      </div>
    </div>

    <v-row class="mb-6 dm-filters-row" align="center">
      <v-col cols="auto">
        <v-select
          v-model="selectedTeamFilter"
          :items="teamFilterOptions"
          item-text="name"
          item-value="id"
          label="Todos os times"
          prepend-inner-icon="mdi-account-group-outline"
          dense
          outlined
          rounded="lg"
          hide-details
          class="dm-input"
          style="min-width: 220px"
          @change="onTeamFilterChange"
        >
          <template v-slot:selection="{ item }">
            <span class="text-body-2 font-weight-medium">{{ item.name }}</span>
          </template>
        </v-select>
      </v-col>

      <v-col cols="auto">
        <v-select
          v-model="selectedUserFilter"
          :items="userFilterOptions"
          item-text="name"
          item-value="id"
          label="Todos os colaboradores"
          prepend-inner-icon="mdi-account-outline"
          :loading="loadingTeamUsers"
          dense
          outlined
          rounded="lg"
          hide-details
          class="dm-input"
          style="min-width: 220px"
          @change="onUserFilterChange"
        >
          <template v-slot:item="{ item }">
            <v-list-item-avatar v-if="item.id !== 'all'" size="28" class="mr-3">
              <v-avatar :color="item.avatarUrl ? 'transparent' : getAvatarColor(item.name)" size="28">
                <v-img v-if="item.avatarUrl" :src="item.avatarUrl" :alt="item.name" />
                <span v-else class="white--text font-weight-bold" style="font-size: 11px">{{ getInitials(item.name) }}</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="text-body-2">{{ item.name }}</v-list-item-title>
            </v-list-item-content>
          </template>
          <template v-slot:selection="{ item }">
            <div class="d-flex align-center">
              <v-avatar v-if="item.id !== 'all'" :color="item.avatarUrl ? 'transparent' : getAvatarColor(item.name)" size="24" class="mr-2">
                <v-img v-if="item.avatarUrl" :src="item.avatarUrl" :alt="item.name" />
                <span v-else class="white--text font-weight-bold" style="font-size: 9px">{{ getInitials(item.name) }}</span>
              </v-avatar>
              <span class="text-body-2 font-weight-medium">{{ item.name }}</span>
            </div>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-menu
          v-model="startDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="filterStartDate"
              label="Data Início"
              prepend-inner-icon="mdi-calendar"
              readonly
              dense
              outlined
              rounded="lg"
              hide-details
              clearable
              class="dm-input"
              v-bind="attrs"
              v-on="on"
              @click:clear="filterStartDate = null"
            />
          </template>
          <v-date-picker
            v-model="filterStartDate"
            @input="startDateMenu = false"
            locale="pt-BR"
            no-title
            color="#22d3ee"
          />
        </v-menu>
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-menu
          v-model="endDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="filterEndDate"
              label="Data Fim"
              prepend-inner-icon="mdi-calendar"
              readonly
              dense
              outlined
              rounded="lg"
              hide-details
              clearable
              class="dm-input"
              v-bind="attrs"
              v-on="on"
              @click:clear="filterEndDate = null"
            />
          </template>
          <v-date-picker
            v-model="filterEndDate"
            :min="filterStartDate"
            @input="endDateMenu = false"
            locale="pt-BR"
            no-title
            color="#22d3ee"
          />
        </v-menu>
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-select
          v-model="quickDateFilter"
          :items="quickDateOptions"
          label="Últimos dias"
          prepend-inner-icon="mdi-calendar-range"
          dense
          outlined
          rounded="lg"
          hide-details
          clearable
          class="dm-input"
          @change="applyQuickFilter"
        />
      </v-col>

      <v-col cols="12" sm="6" md="auto" class="d-flex" style="gap: 8px">
        <v-btn
          color="#22d3ee"
          height="40"
          depressed
          rounded="lg"
          class="px-4 dm-btn-primary black--text font-weight-bold"
          @click="applyDateFilter"
        >
          <v-icon left small>mdi-filter</v-icon>
          Aplicar
        </v-btn>
        <v-btn
          height="40"
          outlined
          rounded="lg"
          class="dm-btn-secondary"
          @click="clearDateFilter"
        >
          Limpar
        </v-btn>
      </v-col>

      <v-spacer />

      <v-col cols="auto">
        <v-btn-toggle
          v-model="viewMode"
          mandatory
          rounded="lg"
          class="dm-toggle-group"
        >
          <v-btn value="geral" height="40" class="text-capitalize font-weight-medium">
            <v-icon left small>mdi-view-dashboard</v-icon>
            Visão Geral
          </v-btn>
          <v-btn value="engenharia" height="40" class="text-capitalize font-weight-medium">
            <v-icon left small>mdi-cog</v-icon>
            Engenharia
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <div v-if="viewMode === 'geral'">
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md class="d-flex">
          <v-card flat class="dm-card stat-card flex-grow-1" @click="goToPDIs">
            <v-card-text class="pa-5">
              <div class="d-flex justify-space-between align-start">
                <div class="text-caption font-weight-medium dm-text-muted text-uppercase tracking-wider">PDIs Pendentes</div>
                <div class="dm-icon-badge bg-cyan">
                  <v-icon color="#22d3ee" size="20">mdi-clipboard-text</v-icon>
                </div>
              </div>
              <div class="text-h3 font-weight-bold mt-3 dm-text-main">{{ stats.pendingPDIs }}</div>
              <div class="text-caption dm-text-muted mt-1">Aprovações necessárias</div>
              <div v-if="stats.pendingPDIsTrend" class="text-caption font-weight-medium mt-2 d-flex align-center" :class="stats.pendingPDIsTrend.startsWith('-') ? 'success--text' : 'error--text'">
                <v-icon small :color="stats.pendingPDIsTrend.startsWith('-') ? 'success' : 'error'" class="mr-1">
                  {{ stats.pendingPDIsTrend.startsWith('-') ? 'mdi-trending-down' : 'mdi-trending-up' }}
                </v-icon>
                {{ stats.pendingPDIsTrend }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md class="d-flex">
          <v-card flat class="dm-card stat-card flex-grow-1">
            <v-card-text class="pa-5">
              <div class="d-flex justify-space-between align-start">
                <div class="text-caption font-weight-medium dm-text-muted text-uppercase tracking-wider">Tarefas Atrasadas</div>
                <div class="dm-icon-badge bg-orange">
                  <v-icon color="#FF9800" size="20">mdi-alert-circle</v-icon>
                </div>
              </div>
              <div class="text-h3 font-weight-bold mt-3 dm-text-main">{{ stats.overdueTasks }}</div>
              <div class="text-caption dm-text-muted mt-1">Atenção requerida</div>
              <div v-if="stats.overdueTasksTrend" class="text-caption font-weight-medium mt-2 d-flex align-center" :class="stats.overdueTasksTrend.startsWith('-') ? 'success--text' : 'error--text'">
                <v-icon small :color="stats.overdueTasksTrend.startsWith('-') ? 'success' : 'error'" class="mr-1">
                  {{ stats.overdueTasksTrend.startsWith('-') ? 'mdi-trending-down' : 'mdi-trending-up' }}
                </v-icon>
                {{ stats.overdueTasksTrend }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md class="d-flex">
          <v-card flat class="dm-card stat-card flex-grow-1" @click="goToOneOnOnes">
            <v-card-text class="pa-5">
              <div class="d-flex justify-space-between align-start">
                <div class="text-caption font-weight-medium dm-text-muted text-uppercase tracking-wider">Reuniões 1:1</div>
                <div class="dm-icon-badge bg-red">
                  <v-icon color="#F44336" size="20">mdi-calendar-account</v-icon>
                </div>
              </div>
              <div class="text-h3 font-weight-bold mt-3 dm-text-main">{{ stats.upcomingOneOnOnes }}</div>
              <div class="text-caption dm-text-muted mt-1">Esta semana</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md class="d-flex">
          <v-card flat class="dm-card stat-card flex-grow-1" @click="goToSkills">
            <v-card-text class="pa-5">
              <div class="d-flex justify-space-between align-start">
                <div class="text-caption font-weight-medium dm-text-muted text-uppercase tracking-wider">Competências</div>
                <div class="dm-icon-badge bg-blue">
                  <v-icon color="#2196F3" size="20">mdi-check-circle</v-icon>
                </div>
              </div>
              <div class="text-h3 font-weight-bold mt-3 dm-text-main">{{ stats.competencias }}</div>
              <div class="text-caption dm-text-muted mt-1">Avaliações pendentes</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md class="d-flex">
          <v-card flat class="dm-card stat-card flex-grow-1" @click="goToFeedbacks">
            <v-card-text class="pa-5">
              <div class="d-flex justify-space-between align-start">
                <div class="text-caption font-weight-medium dm-text-muted text-uppercase tracking-wider">Feedbacks</div>
                <div class="dm-icon-badge bg-green">
                  <v-icon color="#4CAF50" size="20">mdi-comment-multiple</v-icon>
                </div>
              </div>
              <div class="text-h3 font-weight-bold mt-3 dm-text-main">{{ stats.feedbacks }}</div>
              <div class="text-caption dm-text-muted mt-1">Aguardando revisão</div>
              <div v-if="stats.feedbacksTrend" class="text-caption font-weight-medium mt-2 d-flex align-center success--text">
                <v-icon small color="success" class="mr-1">mdi-trending-up</v-icon>
                {{ stats.feedbacksTrend }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-6">
        <v-col cols="12" md="6">
          <v-card flat class="dm-card" style="height: 380px; display: flex; flex-direction: column;">
            <v-card-title class="pa-5 pb-3 border-bottom dm-border-color">
              <span class="text-h6 font-weight-bold dm-text-main">Ações Pendentes</span>
            </v-card-title>
            <v-card-text class="pa-0 flex-grow-1 custom-scrollbar" style="overflow-y: auto;">
              <v-expansion-panels flat accordion class="dm-expansion-panels">
                
                <v-expansion-panel class="dm-expansion-panel">
                  <v-expansion-panel-header class="py-4">
                    <div class="d-flex align-center justify-space-between" style="width: 100%;">
                      <div class="d-flex align-center">
                        <div class="dm-icon-badge small bg-cyan mr-3">
                          <v-icon color="#22d3ee" size="16">mdi-clipboard-text</v-icon>
                        </div>
                        <span class="font-weight-medium dm-text-main">PDIs Pendentes</span>
                      </div>
                      <v-chip small class="dm-chip mr-2 font-weight-bold">{{ stats.pendingPDIs }}</v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-list dense class="dm-list transparent">
                      <v-list-item v-for="pdi in pendingPDIs" :key="pdi.id" class="px-2 rounded-lg">
                        <v-list-item-content>
                          <v-list-item-title class="dm-text-main">
                            <strong class="font-weight-bold">{{ pdi.employeeName }}</strong> - <span class="dm-text-muted">{{ pdi.type }}</span>
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item v-if="pendingPDIs.length === 0">
                        <v-list-item-content>
                          <v-list-item-subtitle class="text-center dm-text-muted py-2">Nenhum PDI pendente</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                    <v-btn block text color="#22d3ee" class="mt-2 rounded-lg dm-btn-text" @click="goToPDIs">
                      Ver Todos <v-icon right small>mdi-arrow-right</v-icon>
                    </v-btn>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel class="dm-expansion-panel">
                  <v-expansion-panel-header class="py-4">
                    <div class="d-flex align-center justify-space-between" style="width: 100%;">
                      <div class="d-flex align-center">
                        <div class="dm-icon-badge small bg-blue mr-3">
                          <v-icon color="#2196F3" size="16">mdi-calendar-account</v-icon>
                        </div>
                        <span class="font-weight-medium dm-text-main">Próximas Reuniões 1:1</span>
                      </div>
                      <v-chip small class="dm-chip mr-2 font-weight-bold">{{ stats.upcomingOneOnOnes }}</v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-list dense class="dm-list transparent">
                      <v-list-item v-for="meeting in nextOneOnOnes" :key="meeting.id" class="px-2 rounded-lg">
                        <v-list-item-avatar size="32" class="my-1 mr-3">
                          <v-avatar :color="meeting.employeeAvatar ? 'transparent' : getAvatarColor(meeting.employeeName)" size="32">
                            <v-img v-if="meeting.employeeAvatar" :src="meeting.employeeAvatar" />
                            <span v-else class="white--text text-caption font-weight-bold">{{ getInitials(meeting.employeeName) }}</span>
                          </v-avatar>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="dm-text-main">
                            <strong class="font-weight-bold">{{ meeting.employeeName }}</strong>
                            <span class="dm-text-muted ml-1">• {{ formatDateTime(meeting.scheduledAt) }}</span>
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item v-if="nextOneOnOnes.length === 0">
                        <v-list-item-content>
                          <v-list-item-subtitle class="text-center dm-text-muted py-2">Nenhuma reunião agendada</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                    <v-btn block text color="#22d3ee" class="mt-2 rounded-lg dm-btn-text" @click="goToOneOnOnes">
                      Ver Todos <v-icon right small>mdi-arrow-right</v-icon>
                    </v-btn>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel class="dm-expansion-panel">
                  <v-expansion-panel-header class="py-4">
                    <div class="d-flex align-center justify-space-between" style="width: 100%;">
                      <div class="d-flex align-center">
                        <div class="dm-icon-badge small bg-orange mr-3">
                          <v-icon color="#FF9800" size="16">mdi-alert-circle</v-icon>
                        </div>
                        <span class="font-weight-medium dm-text-main">Avaliações Atrasadas</span>
                      </div>
                      <v-chip small class="error--text font-weight-bold mr-2" color="rgba(244,67,54,0.15)">
                        {{ stats.overdueEvaluations || 0 }}
                      </v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-list dense class="dm-list transparent">
                      <v-list-item v-for="evaluation in overdueEvaluations" :key="evaluation.id" class="px-2 rounded-lg">
                        <v-list-item-content>
                          <v-list-item-title class="dm-text-main">
                            <strong class="font-weight-bold">{{ evaluation.employeeName }}</strong> - <span class="dm-text-muted">{{ evaluation.type }}</span>
                          </v-list-item-title>
                          <v-list-item-subtitle class="error--text font-weight-medium mt-1">
                            Atrasado há {{ evaluation.daysOverdue }} dias
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item v-if="overdueEvaluations.length === 0">
                        <v-list-item-content>
                          <v-list-item-subtitle class="text-center dm-text-muted py-2">Nenhuma avaliação atrasada</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-expansion-panel-content>
                </v-expansion-panel>

              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card flat class="dm-card" style="height: 380px; display: flex; flex-direction: column;">
            <v-card-title class="pa-5 pb-3">
              <div>
                <div class="text-h6 font-weight-bold dm-text-main">Velocidade do Time</div>
                <div class="text-caption dm-text-muted">Tarefas concluídas por semana</div>
              </div>
            </v-card-title>
            <v-card-text class="px-5 pb-5 pt-0 d-flex flex-column flex-grow-1">
              <div class="flex-grow-1" style="position: relative; min-height: 0;">
                <canvas ref="velocityChart"></canvas>
              </div>
              <div class="d-flex justify-center align-center mt-4">
                <div class="d-flex align-center mr-6">
                  <div style="width: 12px; height: 12px; background-color: #4ade80; border-radius: 3px;" class="mr-2"></div>
                  <span class="text-caption dm-text-main font-weight-medium">Meta atingida</span>
                </div>
                <div class="d-flex align-center">
                  <div style="width: 12px; height: 12px; background-color: #FF9800; border-radius: 3px;" class="mr-2"></div>
                  <span class="text-caption dm-text-main font-weight-medium">Abaixo da meta</span>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="jiraMetrics.connected" class="mb-6">
        <v-col cols="12">
          <v-card flat class="dm-card">
            <v-card-text class="pa-6">
              <div class="d-flex align-start mb-6">
                <div class="dm-icon-badge large bg-primary mr-4">
                  <v-icon color="#22d3ee" size="28">mdi-developer-board</v-icon>
                </div>
                <div class="flex-grow-1">
                  <div class="text-h5 font-weight-bold dm-text-main mb-1">Métricas de Desenvolvimento</div>
                  <div class="text-caption dm-text-muted mb-4">Acompanhamento de tarefas e bugs do time.</div>

                  <v-row class="mb-6" dense>
                    <v-col cols="6" sm="4" md="2">
                      <div class="dm-metric-box">
                        <div class="d-flex align-center mb-2">
                          <v-icon color="#2196F3" small class="mr-2">mdi-progress-clock</v-icon>
                          <span class="text-caption dm-text-muted font-weight-medium">Em Andamento</span>
                        </div>
                        <div class="text-h4 font-weight-bold" style="color: #2196F3;">{{ jiraMetrics.summary.totalInProgress }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6" sm="4" md="2">
                      <div class="dm-metric-box">
                        <div class="d-flex align-center mb-2">
                          <v-icon color="#4CAF50" small class="mr-2">mdi-check-circle</v-icon>
                          <span class="text-caption dm-text-muted font-weight-medium">Concluídos (30d)</span>
                        </div>
                        <div class="text-h4 font-weight-bold" style="color: #4CAF50;">{{ jiraMetrics.summary.totalDoneLast30Days }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6" sm="4" md="2">
                      <div class="dm-metric-box">
                        <div class="d-flex align-center mb-2">
                          <v-icon color="#FF9800" small class="mr-2">mdi-alert-circle</v-icon>
                          <span class="text-caption dm-text-muted font-weight-medium">Atrasados</span>
                        </div>
                        <div class="text-h4 font-weight-bold" style="color: #FF9800;">{{ jiraMetrics.summary.totalOverdue }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6" sm="4" md="2">
                      <div class="dm-metric-box">
                        <div class="d-flex align-center mb-2">
                          <v-icon color="#00BCD4" small class="mr-2">mdi-clipboard-list-outline</v-icon>
                          <span class="text-caption dm-text-muted font-weight-medium">Fila</span>
                        </div>
                        <div class="text-h4 font-weight-bold" style="color: #00BCD4;">{{ jiraMetrics.team.reduce((s, u) => s + (u.metrics.todo || 0), 0) }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6" sm="4" md="2">
                      <div class="dm-metric-box">
                        <div class="d-flex align-center mb-2">
                          <v-icon color="#F44336" small class="mr-2">mdi-bug-outline</v-icon>
                          <span class="text-caption dm-text-muted font-weight-medium">Bugs</span>
                        </div>
                        <div class="text-h4 font-weight-bold" style="color: #F44336;">{{ jiraMetrics.summary.totalBugs }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6" sm="4" md="2">
                      <div class="dm-metric-box">
                        <div class="d-flex align-center mb-2">
                          <v-icon color="#9C27B0" small class="mr-2">mdi-format-list-numbered</v-icon>
                          <span class="text-caption dm-text-muted font-weight-medium">Total Geral</span>
                        </div>
                        <div class="text-h4 font-weight-bold" style="color: #9C27B0;">{{ jiraMetrics.team.reduce((s, u) => s + (u.metrics.totalIssues || 0), 0) }}</div>
                      </div>
                    </v-col>
                  </v-row>

                  <div v-if="jiraMetrics.team.length > 0" class="mb-4">
                    <div class="d-flex align-center justify-space-between mb-4">
                      <div class="text-subtitle-1 font-weight-bold dm-text-main">
                        Membros da Equipe <v-chip small class="dm-chip ml-2">{{ jiraMetrics.team.length }}</v-chip>
                      </div>
                      <v-btn-toggle
                        v-model="jiraMetrics.teamViewMode"
                        mandatory
                        dense
                        rounded="lg"
                        class="dm-toggle-group-small"
                      >
                        <v-btn value="block"><v-icon small>mdi-view-grid-outline</v-icon></v-btn>
                        <v-btn value="list"><v-icon small>mdi-view-list-outline</v-icon></v-btn>
                      </v-btn-toggle>
                    </div>

                    <v-row v-if="jiraMetrics.teamViewMode === 'block'" dense>
                      <v-col v-for="(user, index) in jiraMetrics.team.slice().sort((a,b) => (b.metrics.doneLast30Days||b.metrics.done||0) - (a.metrics.doneLast30Days||a.metrics.done||0))" :key="user.userId" cols="12" sm="6" md="4" lg="3">
                        <div class="dm-user-card pa-4 rounded-xl h-100">
                          <div class="d-flex align-center mb-4">
                            <div class="position-relative mr-3 flex-shrink-0">
                              <v-avatar :color="user.avatarUrl ? 'transparent' : getAvatarColor(user.userName)" size="48">
                                <v-img v-if="user.avatarUrl" :src="user.avatarUrl" />
                                <span v-else class="white--text font-weight-bold">{{ getInitials(user.userName) }}</span>
                              </v-avatar>
                              <div v-if="index < 3" class="dm-medal" :class="`medal-${index+1}`">{{ index + 1 }}</div>
                            </div>
                            <div class="flex-grow-1 overflow-hidden">
                              <div class="font-weight-bold dm-text-main text-truncate">{{ user.userName }}</div>
                              <div class="text-caption dm-text-muted text-truncate">{{ user.userEmail }}</div>
                            </div>
                          </div>
                          
                          <div class="d-flex justify-space-between text-center mt-auto">
                            <div class="dm-stat-mini">
                              <div class="font-weight-bold" style="color: #4CAF50;">{{ user.metrics.done || 0 }}</div>
                              <div class="text-caption dm-text-muted">Concl.</div>
                            </div>
                            <div class="dm-stat-mini">
                              <div class="font-weight-bold" style="color: #2196F3;">{{ user.metrics.inProgress || 0 }}</div>
                              <div class="text-caption dm-text-muted">Andam.</div>
                            </div>
                            <div class="dm-stat-mini">
                              <div class="font-weight-bold" style="color: #00BCD4;">{{ user.metrics.todo || 0 }}</div>
                              <div class="text-caption dm-text-muted">Fila</div>
                            </div>
                            <div class="dm-stat-mini">
                              <div class="font-weight-bold" style="color: #FF9800;">{{ user.metrics.overdueIssues || 0 }}</div>
                              <div class="text-caption dm-text-muted">Atraso</div>
                            </div>
                          </div>
                        </div>
                      </v-col>
                    </v-row>

                    <div v-else class="dm-table-wrapper rounded-lg">
                      <v-simple-table dense class="transparent dm-table">
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left font-weight-bold">Colaborador</th>
                              <th class="text-center font-weight-bold">Total</th>
                              <th class="text-center font-weight-bold">Concluídos</th>
                              <th class="text-center font-weight-bold">Andamento</th>
                              <th class="text-center font-weight-bold">Fila</th>
                              <th class="text-center font-weight-bold">Atrasados</th>
                              <th class="text-center font-weight-bold">Bugs</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(user, index) in jiraMetrics.team.slice().sort((a,b) => (b.metrics.doneLast30Days||b.metrics.done||0) - (a.metrics.doneLast30Days||a.metrics.done||0))" :key="user.userId">
                              <td class="py-2">
                                <div class="d-flex align-center">
                                  <div class="position-relative mr-3">
                                    <v-avatar :color="user.avatarUrl ? 'transparent' : getAvatarColor(user.userName)" size="36">
                                      <v-img v-if="user.avatarUrl" :src="user.avatarUrl" />
                                      <span v-else class="white--text font-weight-bold text-caption">{{ getInitials(user.userName) }}</span>
                                    </v-avatar>
                                    <div v-if="index < 3" class="dm-medal small" :class="`medal-${index+1}`">{{ index + 1 }}</div>
                                  </div>
                                  <div>
                                    <div class="font-weight-bold dm-text-main text-body-2">{{ user.userName }}</div>
                                    <div class="text-caption dm-text-muted">{{ user.userEmail }}</div>
                                  </div>
                                </div>
                              </td>
                              <td class="text-center font-weight-bold dm-text-main">{{ user.metrics.totalIssues || 0 }}</td>
                              <td class="text-center"><v-chip small class="font-weight-bold" color="rgba(76,175,80,0.15)" text-color="#4CAF50">{{ user.metrics.done || 0 }}</v-chip></td>
                              <td class="text-center"><v-chip small class="font-weight-bold" color="rgba(33,150,243,0.15)" text-color="#2196F3">{{ user.metrics.inProgress || 0 }}</v-chip></td>
                              <td class="text-center font-weight-bold dm-text-main">{{ user.metrics.todo || 0 }}</td>
                              <td class="text-center">
                                <v-chip v-if="user.metrics.overdueIssues > 0" small class="font-weight-bold" color="rgba(255,152,0,0.15)" text-color="#FF9800">{{ user.metrics.overdueIssues }}</v-chip>
                                <span v-else class="dm-text-muted">-</span>
                              </td>
                              <td class="text-center font-weight-bold" :class="user.metrics.issuesByType?.Bug > 0 ? 'error--text' : 'dm-text-muted'">{{ user.metrics.issuesByType?.Bug || 0 }}</td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </div>
                  </div>

                  <v-alert v-if="jiraMetrics.team.length === 0 && !jiraMetrics.loading" type="info" text dense rounded="lg" color="#22d3ee" class="mt-4 dm-alert">
                    Nenhum membro da equipe possui tarefas atribuídas ou dados ainda não foram sincronizados.
                  </v-alert>

                  <v-divider class="my-6 dm-border-color"></v-divider>
                  <div class="d-flex align-center flex-wrap justify-space-between" style="gap: 16px">
                    <v-btn rounded="lg" outlined height="40" class="dm-btn-secondary font-weight-medium px-5" @click="toggleJiraAdvanced" :disabled="jiraMetrics.loadingAdvanced">
                      <v-icon left size="20">{{ jiraMetrics.showAdvanced ? 'mdi-chevron-up' : 'mdi-chart-bar' }}</v-icon>
                      {{ jiraMetrics.showAdvanced ? 'Ocultar Detalhes' : 'Análise Avançada' }}
                    </v-btn>

                    <div class="d-flex align-center">
                      <span v-if="jiraMetrics.advancedCachedAt" class="text-caption dm-text-muted mr-3 d-flex align-center">
                        <v-icon x-small color="grey" class="mr-1">mdi-clock-outline</v-icon> Sinc. {{ getAdvancedCacheAge() }}
                      </span>
                      <v-tooltip bottom color="black">
                        <template v-slot:activator="{ on }">
                          <v-btn icon small v-on="on" :loading="jiraMetrics.loadingAdvanced" @click="forceSyncAdvanced" class="dm-icon-btn">
                            <v-icon size="20">mdi-refresh</v-icon>
                          </v-btn>
                        </template>
                        <span>Forçar sincronização</span>
                      </v-tooltip>
                    </div>
                  </div>

                  <v-expand-transition>
                    <div v-if="jiraMetrics.showAdvanced" class="mt-6">
                      <v-progress-linear v-if="jiraMetrics.loadingAdvanced" color="#22d3ee" indeterminate rounded height="4" class="mb-6"></v-progress-linear>

                      <v-row class="mb-4" dense>
                        <v-col cols="6" sm="6" md="3">
                          <div class="dm-adv-metric-box">
                            <v-icon color="#2196F3" size="28" class="mb-2">mdi-timer-sand</v-icon>
                            <div class="text-caption dm-text-muted text-uppercase font-weight-bold tracking-wider mb-1">Lead Time Médio</div>
                            <div class="text-h4 font-weight-bold" style="color: #2196F3;">{{ jiraMetrics.advanced.leadTime.avgLeadTimeDays }}<span class="text-subtitle-1 ml-1">dias</span></div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="6" md="3">
                          <div class="dm-adv-metric-box">
                            <v-icon color="#9C27B0" size="28" class="mb-2">mdi-progress-clock</v-icon>
                            <div class="text-caption dm-text-muted text-uppercase font-weight-bold tracking-wider mb-1">Cycle Time Médio</div>
                            <div class="text-h4 font-weight-bold" style="color: #9C27B0;">{{ jiraMetrics.advanced.cycleTime.avgCycleTimeDays }}<span class="text-subtitle-1 ml-1">dias</span></div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="6" md="3">
                          <div class="dm-adv-metric-box">
                            <v-icon color="#FF9800" size="28" class="mb-2">mdi-speedometer</v-icon>
                            <div class="text-caption dm-text-muted text-uppercase font-weight-bold tracking-wider mb-1">Velocity Time</div>
                            <div class="text-h4 font-weight-bold" style="color: #FF9800;">{{ jiraMetrics.advanced.velocity.teamVelocity.toFixed(1) }}<span class="text-subtitle-1 ml-1">pts</span></div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="6" md="3">
                          <div class="dm-adv-metric-box">
                            <v-icon color="#F44336" size="28" class="mb-2">mdi-refresh-circle</v-icon>
                            <div class="text-caption dm-text-muted text-uppercase font-weight-bold tracking-wider mb-1">Retrabalho</div>
                            <div class="text-h4 font-weight-bold" style="color: #F44336;">{{ ((jiraMetrics.advanced.issuesReopened.totalReopened / Math.max(jiraMetrics.summary.totalDoneLast30Days, 1)) * 100).toFixed(1) }}<span class="text-subtitle-1 ml-1">%</span></div>
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                  </v-expand-transition>

                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-6">
        <v-col cols="12">
          <v-card flat class="dm-card ai-glow-card">
            <v-card-title class="pa-5 pb-4 border-bottom dm-border-color">
              <div class="d-flex align-center justify-space-between w-100">
                <div class="d-flex align-center">
                  <div class="dm-icon-badge bg-cyan mr-4">
                    <v-icon color="#22d3ee" size="24">mdi-brain</v-icon>
                  </div>
                  <div>
                    <div class="text-h5 font-weight-bold dm-text-main" style="line-height: 1.2">ElevMent AI Insights</div>
                    <div v-if="intelligentAnalysis.generatedAt" class="text-caption dm-text-muted mt-1">
                      Gerado em {{ new Date(intelligentAnalysis.generatedAt).toLocaleString('pt-BR') }}
                    </div>
                  </div>
                </div>
                <div class="d-flex align-center">
                  <v-chip outlined color="#22d3ee" class="mr-3 font-weight-bold">
                    <v-icon x-small left>mdi-robot</v-icon>
                    {{ intelligentAnalysis.total || intelligentAnalysis.insights.length }} descobertas
                  </v-chip>
                  <v-btn icon outlined color="#22d3ee" :loading="intelligentAnalysis.generating" @click="regenerateIntelligentAnalysis">
                    <v-icon size="20">mdi-refresh</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card-title>

            <v-card-text class="pa-6">
              <div v-if="intelligentAnalysis.loading || intelligentAnalysis.generating" class="text-center py-10">
                <v-progress-circular indeterminate color="#22d3ee" size="48" width="4" />
                <p class="text-body-1 dm-text-muted mt-4 font-weight-medium">
                  {{ intelligentAnalysis.generating ? 'Analisando dados e gerando insights...' : 'Carregando inteligência...' }}
                </p>
              </div>

              <div v-else-if="!intelligentAnalysis.insights.length" class="text-center py-10">
                <v-icon size="64" color="rgba(255,255,255,0.2)">mdi-brain</v-icon>
                <p class="text-body-1 dm-text-muted mt-4">Nenhum insight gerado para este filtro.</p>
              </div>

              <div v-else>
                <v-expand-transition>
                  <div :key="intelligentAnalysis.currentIndex" class="dm-ai-insight-box">
                    <div class="dm-ai-insight-border" :style="{ backgroundColor: getInsightTypeColor(intelligentAnalysis.insights[intelligentAnalysis.currentIndex].type) }"></div>
                    <div class="pa-5 w-100">
                      <div class="d-flex justify-space-between mb-3">
                        <v-chip x-small :color="getInsightTypeColor(intelligentAnalysis.insights[intelligentAnalysis.currentIndex].type)" text-color="white" class="font-weight-bold text-uppercase px-2">
                          <v-icon x-small left>{{ getInsightTypeIcon(intelligentAnalysis.insights[intelligentAnalysis.currentIndex].type) }}</v-icon>
                          {{ getInsightTypeLabel(intelligentAnalysis.insights[intelligentAnalysis.currentIndex].type) }}
                        </v-chip>
                        <div class="d-flex align-center text-caption font-weight-bold dm-text-muted">
                          <v-icon x-small color="amber" class="mr-1">mdi-star</v-icon>
                          {{ Math.round(intelligentAnalysis.insights[intelligentAnalysis.currentIndex].confidence * 100) }}% Confiança
                        </div>
                      </div>
                      
                      <h3 class="text-h6 font-weight-bold dm-text-main mb-2">{{ intelligentAnalysis.insights[intelligentAnalysis.currentIndex].title }}</h3>
                      <p class="text-body-1 dm-text-muted" style="line-height: 1.6;">{{ intelligentAnalysis.insights[intelligentAnalysis.currentIndex].summary }}</p>
                      
                      <div v-if="intelligentAnalysis.insights[intelligentAnalysis.currentIndex].recommendedActions?.length" class="mt-4 pt-4 border-top dm-border-color">
                        <div class="text-caption font-weight-bold text-uppercase dm-text-muted mb-2 tracking-wider">Próximos Passos Sugeridos</div>
                        <div v-for="(action, i) in intelligentAnalysis.insights[intelligentAnalysis.currentIndex].recommendedActions" :key="i" class="d-flex align-start mb-2">
                          <v-icon small :color="getInsightTypeColor(intelligentAnalysis.insights[intelligentAnalysis.currentIndex].type)" class="mr-2 mt-1">mdi-arrow-right-bottom</v-icon>
                          <span class="text-body-2 dm-text-main font-weight-medium">{{ action }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-expand-transition>

                <div class="d-flex align-center justify-space-between mt-6">
                  <div class="d-flex" style="gap: 8px;">
                    <div v-for="(ins, idx) in intelligentAnalysis.insights" :key="idx" 
                         class="dm-dot" 
                         :class="{ active: idx === intelligentAnalysis.currentIndex }"
                         :style="idx === intelligentAnalysis.currentIndex ? `background-color: ${getInsightTypeColor(ins.type)}; box-shadow: 0 0 10px ${getInsightTypeColor(ins.type)}80` : ''"
                         @click="intelligentAnalysis.currentIndex = idx">
                    </div>
                  </div>
                  <div class="d-flex" style="gap: 8px;">
                    <v-btn height="36" rounded="lg" outlined class="dm-btn-secondary" :disabled="intelligentAnalysis.currentIndex === 0" @click="intelligentAnalysis.currentIndex--">
                      <v-icon small>mdi-chevron-left</v-icon>
                    </v-btn>
                    <v-btn height="36" rounded="lg" depressed class="dm-btn-primary font-weight-bold black--text px-4" :disabled="intelligentAnalysis.currentIndex >= intelligentAnalysis.insights.length - 1" @click="intelligentAnalysis.currentIndex++">
                      Próximo <v-icon right small>mdi-arrow-right</v-icon>
                    </v-btn>
                  </div>
                </div>

                <div class="d-flex flex-wrap mt-6 pt-4 border-top dm-border-color" style="gap: 12px;">
                  <v-btn outlined rounded="lg" class="dm-btn-action" @click="gerarFeedback" :disabled="isViewOnlyMode">
                    <v-icon left size="18">mdi-comment-text-outline</v-icon> Criar Feedback
                  </v-btn>
                  <v-btn outlined rounded="lg" class="dm-btn-action" @click="criarPDI" :disabled="isViewOnlyMode">
                    <v-icon left size="18">mdi-clipboard-text-outline</v-icon> Novo PDI
                  </v-btn>
                  <v-btn outlined rounded="lg" class="dm-btn-action" @click="prepararOneOnOne" :disabled="isViewOnlyMode">
                    <v-icon left size="18">mdi-calendar-account-outline</v-icon> Agendar 1:1
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <div v-else-if="viewMode === 'engenharia'">

      <div v-if="isIndividualMode">
        <template v-if="loadingIndividual">
          <v-row class="mb-6">
            <v-col cols="12">
              <v-card flat class="dm-card py-12 text-center">
                <v-progress-circular indeterminate color="#22d3ee" size="64" width="4" />
                <div class="text-h6 dm-text-main mt-6 font-weight-bold">Processando DORA Metrics...</div>
              </v-card>
            </v-col>
          </v-row>
        </template>

        <template v-else-if="!selectedUserData.miniCards">
          <v-row class="mb-6">
            <v-col cols="12">
              <v-card flat class="dm-card py-12 text-center">
                <v-icon size="80" color="rgba(255,255,255,0.1)">mdi-account-hard-hat</v-icon>
                <div class="text-h5 dm-text-main mt-6 font-weight-bold">Dados individuais em processo</div>
                <div class="text-body-1 dm-text-muted mt-2 mx-auto" style="max-width: 500px;">
                  As métricas aprofundadas deste colaborador estarão disponíveis logo que a sincronização do repositório for concluída.
                </div>
              </v-card>
            </v-col>
          </v-row>
        </template>

        <template v-else>
          <v-card flat class="dm-card mb-6 overflow-hidden">
            <div class="dm-hero-gradient pa-6">
              <v-row align="center">
                <v-col cols="12" md="8" class="d-flex align-center">
                  <v-avatar size="80" :color="selectedUserAvatarUrl ? 'transparent' : 'rgba(255,255,255,0.1)'" class="mr-6 profile-avatar">
                    <v-img v-if="selectedUserAvatarUrl" :src="selectedUserAvatarUrl" />
                    <span v-else class="white--text text-h4 font-weight-bold">{{ getInitials(selectedUserData.name) }}</span>
                  </v-avatar>
                  <div>
                    <div class="d-flex align-center mb-1">
                      <h2 class="text-h4 font-weight-bold white--text mr-3">{{ selectedUserData.name }}</h2>
                      <v-chip dark color="rgba(255,255,255,0.2)" class="font-weight-bold px-4">{{ selectedUserData.squad }}</v-chip>
                    </div>
                    <div class="text-subtitle-1 white--text font-weight-medium" style="opacity: 0.9;">{{ selectedUserData.level }} Developer</div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="text-md-right text-left mt-4 mt-md-0">
                  <div class="text-caption text-uppercase font-weight-bold white--text tracking-wider" style="opacity: 0.8;">Score Técnico Global</div>
                  <div class="d-flex align-center justify-md-end mt-1">
                    <v-icon color="#4ade80" size="32" class="mr-2">mdi-trending-up</v-icon>
                    <span class="text-h2 font-weight-black white--text" style="line-height: 1;">{{ selectedUserData.velocidadeGeral }}</span>
                    <span class="text-h5 white--text ml-1" style="opacity: 0.6; align-self: flex-end; padding-bottom: 4px;">/100</span>
                  </div>
                </v-col>
              </v-row>
            </div>
            
            <div class="pa-4 bg-ai-bar d-flex align-center">
              <v-icon color="#22d3ee" class="mr-3">mdi-creation</v-icon>
              <span class="text-body-2 dm-text-main font-weight-medium">{{ selectedUserData.insightIA }}</span>
            </div>
          </v-card>

          <v-row class="mb-6">
            <v-col v-for="card in selectedUserData.miniCards" :key="card.label" cols="12" sm="6" md="3">
              <v-tooltip top color="#1D1D1F" content-class="dm-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <v-card flat class="dm-card h-100 dm-hover-card" v-bind="attrs" v-on="on">
                    <v-card-text class="pa-5">
                      <div class="d-flex justify-space-between align-center mb-3">
                        <div class="d-flex align-center">
                          <div class="dm-icon-badge mini mr-2" style="background: rgba(255,255,255,0.05);">
                            <v-icon size="16" color="grey">{{ card.icon }}</v-icon>
                          </div>
                          <span class="text-caption text-uppercase font-weight-bold dm-text-muted">{{ card.label }}</span>
                        </div>
                        <v-icon x-small color="#22d3ee">mdi-information-outline</v-icon>
                      </div>
                      <div class="text-h4 font-weight-bold dm-text-main">{{ card.value }}</div>
                      <div class="d-flex align-center justify-space-between mt-3 mb-1">
                        <span class="text-caption dm-text-muted">Desempenho</span>
                        <span class="text-caption font-weight-bold" :style="{ color: card.subColor || '#22d3ee' }">{{ card.subLabel }}</span>
                      </div>
                      <v-progress-linear :value="card.progress" :color="card.progressColor || '#22d3ee'" height="6" rounded background-color="rgba(255,255,255,0.1)"></v-progress-linear>
                    </v-card-text>
                  </v-card>
                </template>
                <div class="d-flex align-start pa-1">
                  <v-icon small color="#22d3ee" class="mr-2 mt-1">mdi-creation</v-icon>
                  <span class="text-body-2">{{ card.tooltip }}</span>
                </div>
              </v-tooltip>
            </v-col>
          </v-row>

          <v-row class="mb-6">
            <v-col cols="12" md="5">
              <v-card flat class="dm-card h-100 d-flex flex-column">
                <v-card-title class="pa-5 pb-0">
                  <span class="text-h6 font-weight-bold dm-text-main">Radar de Skills</span>
                </v-card-title>
                <v-card-text class="flex-grow-1 d-flex flex-column justify-center pt-4">
                  <div style="position: relative; height: 300px; width: 100%;">
                    <canvas ref="individualRadarChart"></canvas>
                  </div>
                  <div class="d-flex justify-center mt-4" style="gap: 20px;">
                    <div class="d-flex align-center">
                      <div class="dm-legend-dot cyan"></div>
                      <span class="text-caption dm-text-main font-weight-medium">{{ selectedUserData.name }}</span>
                    </div>
                    <div class="d-flex align-center">
                      <div class="dm-legend-dot dashed"></div>
                      <span class="text-caption dm-text-muted">Benchmark do Time</span>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="7">
              <v-row class="mb-4" dense>
                <v-col cols="12" sm="6">
                  <v-card flat class="dm-card bg-cyan-tint h-100" style="border-color: rgba(34,211,238,0.3) !important;">
                    <v-card-text class="pa-5">
                      <div class="d-flex align-center mb-3">
                        <v-icon color="#22d3ee" class="mr-2">mdi-trophy-variant</v-icon>
                        <span class="text-caption text-uppercase font-weight-bold" style="color: #22d3ee;">Skill em Destaque</span>
                      </div>
                      <div class="text-h5 font-weight-bold white--text mb-1">{{ selectedUserData.melhorAtributo.name }}</div>
                      <div class="d-flex align-end">
                        <span class="text-h3 font-weight-black" style="color: #4ade80;">{{ selectedUserData.melhorAtributo.score }}</span>
                        <span class="text-subtitle-1 dm-text-muted mb-1 ml-1">/100</span>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-card flat class="dm-card bg-orange-tint h-100" style="border-color: rgba(255,152,0,0.3) !important;">
                    <v-card-text class="pa-5">
                      <div class="d-flex align-center mb-3">
                        <v-icon color="#FF9800" class="mr-2">mdi-target</v-icon>
                        <span class="text-caption text-uppercase font-weight-bold" style="color: #FF9800;">Foco de Desenvolvimento</span>
                      </div>
                      <div class="text-h5 font-weight-bold white--text mb-1">{{ selectedUserData.aDesenvolver.name }}</div>
                      <div class="d-flex align-end">
                        <span class="text-h3 font-weight-black" style="color: #FF9800;">{{ selectedUserData.aDesenvolver.score }}</span>
                        <span class="text-subtitle-1 dm-text-muted mb-1 ml-1">/100</span>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-card flat class="dm-card">
                <v-card-text class="pa-5">
                  <div class="text-h6 font-weight-bold dm-text-main mb-4">Mapeamento de Dimensões</div>
                  <v-tooltip v-for="dim in selectedUserData.dimensoes" :key="dim.name" top color="#1D1D1F" content-class="dm-tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <div class="dm-dimension-row mb-4" v-bind="attrs" v-on="on">
                        <div class="d-flex justify-space-between align-end mb-2">
                          <div class="d-flex align-center">
                            <v-icon size="18" :color="dim.color" class="mr-2">{{ dim.icon }}</v-icon>
                            <span class="text-body-1 font-weight-bold dm-text-main">{{ dim.name }}</span>
                          </div>
                          <div class="text-h6 font-weight-bold" :style="{ color: dim.color }">{{ dim.score }}</div>
                        </div>
                        <v-progress-linear :value="dim.score" :color="dim.color" height="8" rounded background-color="rgba(255,255,255,0.05)"></v-progress-linear>
                      </div>
                    </template>
                    <div class="d-flex align-start pa-1">
                      <v-icon small color="#22d3ee" class="mr-2 mt-1">mdi-creation</v-icon>
                      <span class="text-body-2">{{ dim.tooltip }}</span>
                    </div>
                  </v-tooltip>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </div>

      <v-row class="mb-6 mt-2">
        <v-col cols="12" sm="6" md="3">
          <v-card flat class="dm-card dm-metric-block">
            <v-card-text class="pa-5">
              <div class="d-flex align-start justify-space-between mb-4">
                <div class="dm-icon-badge bg-cyan"><v-icon color="#22d3ee" size="24">mdi-truck-delivery</v-icon></div>
                <div class="text-right">
                  <div class="text-h4 font-weight-bold dm-text-main">{{ engineeringMetrics.entrega.score }}<span class="text-h6 dm-text-muted">/100</span></div>
                </div>
              </div>
              <div class="text-body-1 font-weight-bold dm-text-main mb-1">Flow de Entrega</div>
              <div class="text-caption dm-text-muted mb-3">{{ engineeringMetrics.entrega.description }}</div>
              <v-progress-linear :value="engineeringMetrics.entrega.score" color="#22d3ee" height="4" rounded background-color="rgba(255,255,255,0.1)"></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>
        
        <v-col cols="12" sm="6" md="3">
          <v-card flat class="dm-card dm-metric-block">
            <v-card-text class="pa-5">
              <div class="d-flex align-start justify-space-between mb-4">
                <div class="dm-icon-badge bg-orange"><v-icon color="#FF9800" size="24">mdi-shield-check</v-icon></div>
                <div class="text-right">
                  <div class="text-h4 font-weight-bold dm-text-main">{{ engineeringMetrics.qualidade.score }}<span class="text-h6 dm-text-muted">/100</span></div>
                </div>
              </div>
              <div class="text-body-1 font-weight-bold dm-text-main mb-1">Qualidade do Código</div>
              <div class="text-caption dm-text-muted mb-3">{{ engineeringMetrics.qualidade.description }}</div>
              <v-progress-linear :value="engineeringMetrics.qualidade.score" color="#FF9800" height="4" rounded background-color="rgba(255,255,255,0.1)"></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card flat class="dm-card dm-metric-block">
            <v-card-text class="pa-5">
              <div class="d-flex align-start justify-space-between mb-4">
                <div class="dm-icon-badge bg-blue"><v-icon color="#2196F3" size="24">mdi-account-group</v-icon></div>
                <div class="text-right">
                  <div class="text-h4 font-weight-bold dm-text-main">{{ engineeringMetrics.colaboracao.score }}<span class="text-h6 dm-text-muted">/100</span></div>
                </div>
              </div>
              <div class="text-body-1 font-weight-bold dm-text-main mb-1">Índice de Colaboração</div>
              <div class="text-caption dm-text-muted mb-3">{{ engineeringMetrics.colaboracao.description }}</div>
              <v-progress-linear :value="engineeringMetrics.colaboracao.score" color="#2196F3" height="4" rounded background-color="rgba(255,255,255,0.1)"></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card flat class="dm-card dm-metric-block">
            <v-card-text class="pa-5">
              <div class="d-flex align-start justify-space-between mb-4">
                <div class="dm-icon-badge bg-purple"><v-icon color="#9C27B0" size="24">mdi-school</v-icon></div>
                <div class="text-right">
                  <div class="text-h4 font-weight-bold dm-text-main">{{ engineeringMetrics.evolucaoTecnica.score }}<span class="text-h6 dm-text-muted">/100</span></div>
                </div>
              </div>
              <div class="text-body-1 font-weight-bold dm-text-main mb-1">Evolução Técnica</div>
              <div class="text-caption dm-text-muted mb-3">{{ engineeringMetrics.evolucaoTecnica.description }}</div>
              <v-progress-linear :value="engineeringMetrics.evolucaoTecnica.score" color="#9C27B0" height="4" rounded background-color="rgba(255,255,255,0.1)"></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <div class="d-flex align-center justify-space-between mb-6">
        <div>
          <h3 class="text-h5 font-weight-bold dm-text-main">Métricas Fixadas</h3>
          <p class="text-caption dm-text-muted mb-0">Customize este painel com as métricas mais relevantes do catálogo.</p>
        </div>
        <v-btn depressed color="#22d3ee" rounded="lg" class="black--text font-weight-bold px-5" @click="metricsCatalogOpen = true">
          <v-icon left size="20">mdi-plus-box-multiple-outline</v-icon> Adicionar Gráficos
        </v-btn>
      </div>

      <div v-if="totalFavoriteMetrics === 0" class="dm-empty-state text-center py-12 rounded-xl mb-6">
        <v-icon size="64" color="rgba(255,255,255,0.1)">mdi-view-dashboard-variant-outline</v-icon>
        <div class="text-h6 dm-text-main font-weight-bold mt-4">Painel Limpo</div>
        <div class="text-body-1 dm-text-muted mt-1 mb-6">Você ainda não possui métricas fixadas para exibição rápida.</div>
        <v-btn outlined color="#22d3ee" rounded="lg" @click="metricsCatalogOpen = true">
          Explorar Catálogo DORA
        </v-btn>
      </div>

      <div v-for="group in favoriteMetricsGrouped" :key="group.source" class="mb-8">
        <div class="d-flex align-center mb-4 pl-1">
          <v-icon :color="getSourceColor(group.source)" class="mr-2">{{ getSourceIcon(group.source) }}</v-icon>
          <span class="text-subtitle-1 font-weight-bold dm-text-main mr-3">{{ group.source }}</span>
          <v-chip small class="dm-chip">{{ group.metrics.length }} ativas</v-chip>
        </div>

        <v-row>
          <v-col v-for="metric in group.metrics" :key="metric.id" cols="12" md="6">
            <v-card flat class="dm-card h-100 d-flex flex-column">
              <v-card-title class="pa-5 pb-2 d-flex justify-space-between align-start">
                <div>
                  <div class="text-subtitle-1 font-weight-bold dm-text-main" style="line-height: 1.2">{{ metric.name }}</div>
                  <div class="text-caption dm-text-muted mt-1">{{ metric.description }}</div>
                </div>
                <v-btn icon small @click="toggleFavoriteMetric(metric.id)">
                  <v-icon color="#22d3ee">mdi-star</v-icon>
                </v-btn>
              </v-card-title>
              
              <v-card-text class="flex-grow-1 px-5 pb-5 pt-2">
                <div style="position: relative; height: 260px; width: 100%;">
                  <canvas :ref="'favoriteChart_' + metric.id"></canvas>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </div>

    <v-navigation-drawer
      v-model="metricsCatalogOpen"
      temporary
      right
      width="450"
      fixed
      class="dm-drawer"
    >
      <div class="d-flex flex-column h-100">
        <div class="pa-6 border-bottom dm-border-color shrink-0">
          <div class="d-flex justify-space-between align-start mb-2">
            <div>
              <h3 class="text-h5 font-weight-bold dm-text-main">Métricas</h3>
              <p class="text-body-2 dm-text-muted mb-0">DORA, Agilidade e Qualidade</p>
            </div>
            <v-btn icon @click="metricsCatalogOpen = false" class="dm-icon-btn">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="pa-6 pb-2 shrink-0 bg-drawer-filters">
          <v-text-field
            v-model="metricsCatalogSearch"
            placeholder="Buscar..."
            outlined
            dense
            rounded="lg"
            hide-details
            prepend-inner-icon="mdi-magnify"
            clearable
            class="dm-input mb-4"
          ></v-text-field>

          <div class="mb-3">
            <div class="text-caption font-weight-bold text-uppercase dm-text-muted mb-2 tracking-wider">Fonte de Dados</div>
            <div class="d-flex flex-wrap" style="gap: 8px;">
              <v-chip v-for="src in catalogSourceOptions" :key="src"
                :class="metricsCatalogSourceFilter === src ? 'dm-chip-active' : 'dm-chip'"
                small class="font-weight-bold" @click="metricsCatalogSourceFilter = src"
              >{{ src }}</v-chip>
            </div>
          </div>
          
          <div>
            <div class="text-caption font-weight-bold text-uppercase dm-text-muted mb-2 tracking-wider">Categoria</div>
            <div class="d-flex flex-wrap" style="gap: 8px;">
              <v-chip v-for="cat in catalogCategoryOptions" :key="cat"
                :class="metricsCatalogCategoryFilter === cat ? 'dm-chip-active' : 'dm-chip'"
                small class="font-weight-bold" @click="metricsCatalogCategoryFilter = cat"
              >{{ cat }}</v-chip>
            </div>
          </div>
        </div>

        <div class="flex-grow-1 overflow-y-auto pa-4 custom-scrollbar">
          <div v-if="filteredCatalogGrouped.length === 0" class="text-center py-10">
            <v-icon size="48" color="rgba(255,255,255,0.1)">mdi-database-search</v-icon>
            <p class="text-body-2 dm-text-muted mt-4">Nenhuma métrica encontrada para os filtros aplicados.</p>
          </div>

          <div v-for="group in filteredCatalogGrouped" :key="group.source" class="mb-6">
            <div class="d-flex align-center mb-3 pl-2">
              <v-icon small :color="getSourceColor(group.source)" class="mr-2">{{ getSourceIcon(group.source) }}</v-icon>
              <span class="text-body-2 font-weight-bold dm-text-main text-uppercase tracking-wider">{{ group.source }}</span>
            </div>
            
            <v-card flat class="dm-card bg-transparent border-0 pa-0">
              <v-list class="transparent pa-0">
                <v-list-item v-for="metric in group.metrics" :key="metric.id" class="dm-list-item mb-2 rounded-lg pa-3">
                  <v-list-item-content class="pa-0">
                    <div class="d-flex justify-space-between align-start">
                      <div class="pr-3">
                        <div class="text-body-2 font-weight-bold dm-text-main mb-1">{{ metric.name }}</div>
                        <div class="text-caption dm-text-muted mb-2" style="line-height: 1.4;">{{ metric.description }}</div>
                        <v-chip x-small class="dm-chip-category" :style="`color: ${getCategoryColor(metric.category)}; border-color: ${getCategoryColor(metric.category)}40; background: ${getCategoryColor(metric.category)}10;`">{{ metric.category }}</v-chip>
                      </div>
                      <v-btn icon @click="toggleFavoriteMetric(metric.id)" class="dm-icon-btn shrink-0" :class="{ 'active': isMetricFavorite(metric.id) }">
                        <v-icon :color="isMetricFavorite(metric.id) ? '#22d3ee' : 'rgba(255,255,255,0.2)'">{{ isMetricFavorite(metric.id) ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
                      </v-btn>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>
          </div>
        </div>
      </div>
    </v-navigation-drawer>
  </v-container>
</template>

<script>
// O SCRIPT FOI MANTIDO EXATAMENTE IGUAL PARA PRESERVAR 100% DA LÓGICA E INTEGRAÇÕES
import { ManagerService, JiraService, IntegrationsService, UsersService, TeamsService } from '@/services'
import Chart from 'chart.js'
import globalViewMode from '@/mixins/globalViewMode'

export default {
  name: 'DashboardManager',

  mixins: [globalViewMode],

  data: () => ({
    // Modo de visualização
    viewMode: 'geral', // 'geral' ou 'engenharia'
    
    // Filtros de time e pessoa
    selectedTeamFilter: 'all',
    selectedUserFilter: 'all',

    // Dados reais da API (times e usuários ativos)
    apiTeams: [],
    apiUsers: [],
    // Membros do time selecionado (carregado via TeamsService.getMembers)
    teamFilteredUsers: [],
    loadingTeamUsers: false,

    // Dados reais da análise individual (carregado via ManagerService.getIndividualAnalysis)
    individualAnalysisData: null,
    loadingIndividual: false,

    // Mock: lista de times (mantido para dados de demo na análise individual)
    mockTeams: [
      { id: 'all', name: 'Todos os Times' },
      { id: 'team-frontend', name: 'Frontend' },
      { id: 'team-backend', name: 'Backend' },
      { id: 'team-mobile', name: 'Mobile' },
      { id: 'team-devops', name: 'DevOps' },
    ],

    // Mock: lista de usuários
    mockUsers: [
      { id: 'all', name: 'Todos os Colaboradores', teamId: null },
      { id: 'user-1', name: 'Ana Silva', teamId: 'team-frontend', squad: 'Frontend', level: 'Elite', velocidadeGeral: 95, insightIA: 'Volume 4% acima da média. Consistência alta — entregou todas as sprints sem atraso nos últimos 60 dias.', miniCards: [
        { label: 'Tasks', icon: 'mdi-cog-transfer', value: '28', progress: 80, progressColor: '#4CAF50', subLabel: 'time: 27', subColor: '#4CAF50', tooltip: 'Volume 4% acima da média. Consistência alta — entregou todas as sprints sem atraso nos últimos 60 dias.' },
        { label: 'PRs', icon: 'mdi-source-pull', value: '12', progress: 50, progressColor: '#2196F3', subLabel: 'time: 12', subColor: '#757575', tooltip: 'PRs com tamanho médio ideal (~180 linhas). Boa prática de quebrar entregas em PRs menores.' },
        { label: 'Reviews', icon: 'mdi-eye-check', value: '45', progress: 70, progressColor: '#4CAF50', subLabel: 'time: 35', subColor: '#4CAF50', tooltip: 'Top reviewer do time. Reviews detalhados com comentários construtivos — referência para o time.' },
        { label: 'Cycle', icon: 'mdi-clock-outline', value: '1.8d', progress: 85, progressColor: '#4CAF50', subLabel: 'time: 1.85d', subColor: '#4CAF50', tooltip: 'Cycle time 23% menor que a média. Reflexo de PRs bem descritos e comunicação eficiente.' },
      ], radar: { user: [92, 88, 95, 90, 93], team: [85, 83, 82, 84, 80] }, melhorAtributo: { name: 'Colaboração', score: 95 }, aDesenvolver: { name: 'Qualidade', score: 88 }, dimensoes: [
        { name: 'Entrega', score: 92, color: '#22d3ee', icon: 'mdi-flash', tooltip: 'Entrega consistente e previsível. Não tem picos nem vales — padrão de profissional sênior maduro.' },
        { name: 'Qualidade', score: 88, color: '#4CAF50', icon: 'mdi-star-circle', tooltip: 'Apenas 2% de PRs reabertos no trimestre. Cobertura de testes acima de 85% nos PRs.' },
        { name: 'Colaboração', score: 95, color: '#22d3ee', icon: 'mdi-eye-check', tooltip: 'Participa de reviews cross-team. Mentora 2 devs juniores informalmente — considerar formalizar.' },
        { name: 'Evolução', score: 90, color: '#FF9800', icon: 'mdi-trending-up', tooltip: 'Migrou 3 componentes para nova arquitetura. Demonstra capacidade de absorver complexidade crescente.' },
      ], analiseIA: { texto: 'Ana demonstra performance excepcional em todas as dimensões. Nos últimos 90 dias, seu tempo de PR reduziu 20% enquanto a qualidade se manteve alta. Excelente colaboradora com reviews consistentes.', pontos: [
        { text: 'Pronta para assumir papel de tech lead', color: '#424242' },
        { text: 'Pode mentorar membros mais novos do time', color: '#22d3ee' },
        { text: 'Considerar aumento de escopo em arquitetura', color: '#22d3ee' },
      ] } },
      { id: 'user-2', name: 'Carlos Lima', teamId: 'team-backend', squad: 'Backend', level: 'Sênior', velocidadeGeral: 88, insightIA: 'Bom desempenho geral. Tempo de review pode melhorar — média 15% acima do time.', miniCards: [
        { label: 'Tasks', icon: 'mdi-cog-transfer', value: '24', progress: 70, progressColor: '#4CAF50', subLabel: 'time: 27', subColor: '#757575', tooltip: 'Entrega 11% abaixo da média do time. Forte em tarefas complexas, porém volume menor.' },
        { label: 'PRs', icon: 'mdi-source-pull', value: '10', progress: 45, progressColor: '#2196F3', subLabel: 'time: 12', subColor: '#757575', tooltip: 'PRs grandes (~350 linhas). Considerar dividir em PRs menores para facilitar review.' },
        { label: 'Reviews', icon: 'mdi-eye-check', value: '35', progress: 55, progressColor: '#FF9800', subLabel: 'time: 35', subColor: '#FF9800', tooltip: 'Participação mediana em reviews. Pode contribuir mais para a qualidade do time.' },
        { label: 'Cycle', icon: 'mdi-clock-outline', value: '2.1d', progress: 65, progressColor: '#FF9800', subLabel: 'time: 1.85d', subColor: '#FF9800', tooltip: 'Cycle time 14% acima da média. PRs grandes contribuem para tempo elevado.' },
      ], radar: { user: [88, 85, 82, 86, 84], team: [85, 83, 82, 84, 80] }, melhorAtributo: { name: 'Entrega', score: 88 }, aDesenvolver: { name: 'Colaboração', score: 82 }, dimensoes: [
        { name: 'Entrega', score: 88, color: '#22d3ee', icon: 'mdi-flash', tooltip: 'Entregas consistentes com foco em qualidade. Prioriza complexidade sobre volume.' },
        { name: 'Qualidade', score: 85, color: '#4CAF50', icon: 'mdi-star-circle', tooltip: 'Boa cobertura de testes. 5% de PRs reabertos — espaço para melhoria.' },
        { name: 'Colaboração', score: 82, color: '#22d3ee', icon: 'mdi-eye-check', tooltip: 'Faz reviews internos mas não participa cross-team. Oportunidade de crescimento.' },
        { name: 'Evolução', score: 86, color: '#FF9800', icon: 'mdi-trending-up', tooltip: 'Bom domínio de arquitetura backend. Aprendendo novos patterns de microserviços.' },
      ], analiseIA: { texto: 'Carlos apresenta entrega consistente com boa qualidade. Oportunidades de melhoria em colaboração — participar mais ativamente de code reviews do time.', pontos: [
        { text: 'Forte em entregas individuais', color: '#424242' },
        { text: 'Aumentar participação em reviews', color: '#22d3ee' },
        { text: 'Potencial para mentoria em backend', color: '#22d3ee' },
      ] } },
      { id: 'user-3', name: 'Maria Santos', teamId: 'team-frontend', squad: 'Frontend', level: 'Pleno', velocidadeGeral: 82, insightIA: 'Progresso constante nos últimos 60 dias. Cycle time acima da média do time.', miniCards: [
        { label: 'Tasks', icon: 'mdi-cog-transfer', value: '22', progress: 60, progressColor: '#FF9800', subLabel: 'time: 27', subColor: '#FF9800', tooltip: 'Volume 19% abaixo do time. Em evolução — cresceu 8% no último mês.' },
        { label: 'PRs', icon: 'mdi-source-pull', value: '8', progress: 40, progressColor: '#2196F3', subLabel: 'time: 12', subColor: '#757575', tooltip: 'PRs bem estruturados com boa descrição. Volume pode aumentar com mais confiança.' },
        { label: 'Reviews', icon: 'mdi-eye-check', value: '30', progress: 50, progressColor: '#FF9800', subLabel: 'time: 35', subColor: '#FF9800', tooltip: 'Reviews detalhados e construtivos. Referência em feedback positivo no time.' },
        { label: 'Cycle', icon: 'mdi-clock-outline', value: '2.5d', progress: 50, progressColor: '#FF9800', subLabel: 'time: 1.85d', subColor: '#F44336', tooltip: 'Cycle time 35% acima da média. Relacionado a complexidade de tasks atribuídas.' },
      ], radar: { user: [80, 78, 85, 82, 79], team: [85, 83, 82, 84, 80] }, melhorAtributo: { name: 'Colaboração', score: 85 }, aDesenvolver: { name: 'Qualidade', score: 78 }, dimensoes: [
        { name: 'Entrega', score: 80, color: '#22d3ee', icon: 'mdi-flash', tooltip: 'Entregas melhorando gradualmente. Foco em tasks de complexidade média.' },
        { name: 'Qualidade', score: 78, color: '#4CAF50', icon: 'mdi-star-circle', tooltip: 'Cobertura de testes em 65%. Precisa investir mais em testes unitários.' },
        { name: 'Colaboração', score: 85, color: '#22d3ee', icon: 'mdi-eye-check', tooltip: 'Excelente em reviews — comentários detalhados e construtivos. Ponto forte.' },
        { name: 'Evolução', score: 82, color: '#FF9800', icon: 'mdi-trending-up', tooltip: 'Aprendendo React patterns avançados. Boa absorção de feedback técnico.' },
      ], analiseIA: { texto: 'Maria está em evolução constante. Seu ponto forte é colaboração — reviews bem detalhados. Precisa melhorar velocidade de entregas e reduzir cycle time.', pontos: [
        { text: 'Boa colaboradora em code reviews', color: '#424242' },
        { text: 'Reduzir cycle time em 20%', color: '#22d3ee' },
        { text: 'Investir em automação de testes', color: '#22d3ee' },
      ] } },
      { id: 'user-4', name: 'Pedro Costa', teamId: 'team-backend', squad: 'Backend', level: 'Júnior', velocidadeGeral: 76, insightIA: 'Desenvolvedor em ramp-up. Métricas melhorando mês a mês — acompanhar evolução.', miniCards: [
        { label: 'Tasks', icon: 'mdi-cog-transfer', value: '18', progress: 50, progressColor: '#FF9800', subLabel: 'time: 27', subColor: '#F44336', tooltip: 'Volume 33% abaixo do time. Esperado para nível júnior em ramp-up.' },
        { label: 'PRs', icon: 'mdi-source-pull', value: '6', progress: 30, progressColor: '#F44336', subLabel: 'time: 12', subColor: '#F44336', tooltip: 'PRs pequenos mas com necessidade de revisões. Pair programming recomendado.' },
        { label: 'Reviews', icon: 'mdi-eye-check', value: '20', progress: 40, progressColor: '#FF9800', subLabel: 'time: 35', subColor: '#F44336', tooltip: 'Participa pouco de reviews. Oportunidade de aprender com código dos seniors.' },
        { label: 'Cycle', icon: 'mdi-clock-outline', value: '3.2d', progress: 35, progressColor: '#F44336', subLabel: 'time: 1.85d', subColor: '#F44336', tooltip: 'Cycle time 73% acima da média. Normal para ramp-up — monitorar tendência.' },
      ], radar: { user: [72, 70, 75, 78, 68], team: [85, 83, 82, 84, 80] }, melhorAtributo: { name: 'Evolução', score: 78 }, aDesenvolver: { name: 'Consistência', score: 68 }, dimensoes: [
        { name: 'Entrega', score: 72, color: '#22d3ee', icon: 'mdi-flash', tooltip: 'Entrega irregular — picos e vales. Precisa de acompanhamento em planning.' },
        { name: 'Qualidade', score: 70, color: '#4CAF50', icon: 'mdi-star-circle', tooltip: 'Cobertura de testes baixa (40%). Priorizar aprendizado de testes unitários.' },
        { name: 'Colaboração', score: 75, color: '#22d3ee', icon: 'mdi-eye-check', tooltip: 'Aberto a feedback. Precisa participar mais ativamente de code reviews.' },
        { name: 'Evolução', score: 78, color: '#FF9800', icon: 'mdi-trending-up', tooltip: 'Melhor dimensão — crescimento consistente. Absorve bem conhecimento técnico.' },
      ], analiseIA: { texto: 'Pedro está na fase de ramp-up e mostra progresso. Precisa de acompanhamento mais próximo em qualidade de código e reviews. Recomendável pair programming.', pontos: [
        { text: 'Progresso consistente mês a mês', color: '#424242' },
        { text: 'Precisa de pair programming', color: '#22d3ee' },
        { text: 'Acompanhar qualidade com mais reviews', color: '#22d3ee' },
      ] } },
      { id: 'user-5', name: 'Julia Ferreira', teamId: 'team-mobile', squad: 'Mobile', level: 'Sênior', velocidadeGeral: 91, insightIA: 'Excelente performance no time Mobile. Referência em qualidade de código e testes.', miniCards: [
        { label: 'Tasks', icon: 'mdi-cog-transfer', value: '26', progress: 75, progressColor: '#4CAF50', subLabel: 'time: 27', subColor: '#4CAF50', tooltip: 'Volume próximo da média. Focada em tasks de alta complexidade no Mobile.' },
        { label: 'PRs', icon: 'mdi-source-pull', value: '11', progress: 48, progressColor: '#2196F3', subLabel: 'time: 12', subColor: '#757575', tooltip: 'PRs bem dimensionados (~200 linhas). Excelente descrição e contexto nos PRs.' },
        { label: 'Reviews', icon: 'mdi-eye-check', value: '40', progress: 65, progressColor: '#4CAF50', subLabel: 'time: 35', subColor: '#4CAF50', tooltip: 'Ativa em reviews com feedback técnico valioso. Referência em padrões Mobile.' },
        { label: 'Cycle', icon: 'mdi-clock-outline', value: '1.5d', progress: 90, progressColor: '#4CAF50', subLabel: 'time: 1.85d', subColor: '#4CAF50', tooltip: 'Melhor cycle time do time. PRs claros aceleram o processo de review.' },
      ], radar: { user: [90, 92, 88, 91, 89], team: [85, 83, 82, 84, 80] }, melhorAtributo: { name: 'Qualidade', score: 92 }, aDesenvolver: { name: 'Colaboração', score: 88 }, dimensoes: [
        { name: 'Entrega', score: 90, color: '#22d3ee', icon: 'mdi-flash', tooltip: 'Entregas previsíveis e de alta qualidade. Raramente atrasa sprints.' },
        { name: 'Qualidade', score: 92, color: '#4CAF50', icon: 'mdi-star-circle', tooltip: 'Cobertura de testes acima de 90%. Referência em qualidade no time Mobile.' },
        { name: 'Colaboração', score: 88, color: '#22d3ee', icon: 'mdi-eye-check', tooltip: 'Faz reviews internos com qualidade. Pode expandir para reviews cross-team.' },
        { name: 'Evolução', score: 91, color: '#FF9800', icon: 'mdi-trending-up', tooltip: 'Domínio avançado de Swift/Kotlin. Liderando migração para nova arquitetura.' },
      ], analiseIA: { texto: 'Julia é referência em qualidade no time Mobile. Cobertura de testes acima de 90%. Pode ampliar escopo de reviews cross-team.', pontos: [
        { text: 'Referência em testes e qualidade', color: '#424242' },
        { text: 'Expandir reviews para outros times', color: '#22d3ee' },
        { text: 'Potencial para tech lead Mobile', color: '#22d3ee' },
      ] } },
    ],

    // Instância do radar chart individual
    individualRadarChartInstance: null,

    // Filtro de data
    filterStartDate: null,
    filterEndDate: null,
    startDateMenu: false,
    endDateMenu: false,
    quickDateFilter: null,
    quickDateOptions: [
      { text: 'Últimos 7 dias', value: 7 },
      { text: 'Últimos 15 dias', value: 15 },
      { text: 'Últimos 30 dias', value: 30 },
      { text: 'Últimos 60 dias', value: 60 },
      { text: 'Últimos 90 dias', value: 90 },
      { text: 'Último ano', value: 365 },
    ],

    // Estatísticas dos cards superiores
    stats: {
      pendingPDIs: 0,
      pendingPDIsTrend: '',
      overdueTasks: 0,
      overdueTasksTrend: '',
      upcomingOneOnOnes: 0,
      overdueEvaluations: 0,
      competencias: 0,
      feedbacks: 0,
      feedbacksTrend: '',
      oneOnOnePercentage: 0,
      oneOnOneMonths: 0,
    },

    // PDIs pendentes
    pendingPDIs: [],

    // Próximas reuniões 1:1
    nextOneOnOnes: [],

    // Avaliações Atrasadas
    overdueEvaluations: [],

    // Produtividade
    productivity: {
      overdueTasks: 0,
      overdueEmployees: 0,
      tasksLast7Days: 0,
      tasksLast30Days: 0,
    },

    // Análise Inteligente com IA
    intelligentAnalysis: {
      insights: [],
      total: 0,
      generatedAt: null,
      loading: false,
      generating: false,
      currentIndex: 0,
    },

    // Velocidade do Time
    velocityData: {
      weeks: [],
      tasks: [],
      meta: 0
    },

    velocityChart: null, // Referência ao gráfico Chart.js
    radarChart: null, // Referência ao gráfico radar de competências

    // ==================== DADOS VISÃO ENGENHARIA ====================
    
    // Métricas de Engenharia - 4 cards superiores
    engineeringMetrics: {
      entrega: {
        score: 0,
        description: '',
        trend: ''
      },
      qualidade: {
        score: 0,
        description: '',
        trend: ''
      },
      colaboracao: {
        score: 0,
        description: '',
        trend: ''
      },
      evolucaoTecnica: {
        score: 0,
        description: '',
        trend: ''
      }
    },

    // Competências de Engenharia - Gráfico Radar
    engineeringCompetencies: {
      categories: [],
      currentPeriod: [],
      previousPeriod: []
    },

    // Métricas de Desenvolvimento
    jiraMetrics: {
      connected: false,
      loading: false,
      loadingAdvanced: false,
      advancedCachedAt: null,          // timestamp do último cache (localStorage)
      sectionLoading: {                  // loading individual por seção
        leadTime: false,
        cycleTime: false,
        velocity: false,
        created: false,
        reopened: false,
        comments: false,
        projects: false
      },
      tabDataLoaded: {                   // lazy loading por aba
        timing: false,
        velocity: false,
        projects: false
      },
      summary: {
        totalUsers: 0,
        totalInProgress: 0,
        totalDoneLast30Days: 0,
        totalOverdue: 0,
        totalBugs: 0,
        totalStories: 0,
        avgIssuesPerUser: 0,
        // Novas métricas estendidas
        totalIssuesCreated: 0,
        totalIssuesReopened: 0,
        totalComments: 0,
        totalStoryPoints: 0,
        avgStoryPointsPerUser: 0
      },
      team: [],
      topPerformers: [],
      showDetails: false,
      teamViewMode: 'block', // 'block' ou 'list'
      showAdvanced: false,
      // Métricas avançadas
      advanced: {
        leadTime: {
          avgLeadTimeDays: 0,
          userLeadTimes: []
        },
        cycleTime: {
          avgCycleTimeDays: 0,
          userCycleTimes: []
        },
        velocity: {
          teamVelocity: 0,
          totalStoryPoints: 0,
          userVelocities: []
        },
        issuesCreated: {
          totalIssuesCreated: 0,
          userMetrics: []
        },
        issuesReopened: {
          totalReopened: 0,
          userMetrics: []
        },
        comments: {
          totalComments: 0,
          avgCommentsPerUser: 0,
          userMetrics: []
        },
        projects: {
          total: 0,
          projects: []
        }
      }
    },

    // Loading states
    loading: false,
    loadingEngineering: false,
    
    // Jira Advanced Tab
    jiraAdvancedTab: 0,

    // ==================== MÉTRICAS FAVORITAS ====================

    // IDs das métricas favoritadas pelo gestor (carregado do backend)
    favoriteMetricIds: [],
    loadingFavorites: false,

    // Instâncias de gráficos das métricas favoritas
    favoriteChartInstances: {},

    // Dados carregados do backend por metricId
    favoriteMetricsData: {},

    // Estado do catálogo de métricas
    metricsCatalogOpen: false,
    metricsCatalogSearch: '',
    metricsCatalogSourceFilter: 'Todos',
    metricsCatalogCategoryFilter: 'Todas',

    // Opções de filtro do catálogo
    catalogSourceOptions: ['Todos', 'GitHub', 'Jira', 'Azure Pipelines', 'SonarQube'],
    catalogCategoryOptions: ['Todas', 'Code Review', 'Entrega', 'Qualidade', 'CI/CD', 'Produtividade'],

    // Catálogo completo de métricas disponíveis
    allAvailableMetrics: [
      // GitHub
      {
        id: 'github-pr-cycle-time',
        name: 'PR Cycle Time por Repositório',
        description: 'Tempo médio em horas do PR aberto até merge',
        source: 'GitHub',
        category: 'Code Review',
        chartType: 'line',
        legend: [
          { label: 'Frontend', color: '#FF5722' },
          { label: 'Backend', color: '#2196F3' },
          { label: 'Mobile', color: '#FF9800' },
          { label: 'Média Geral', color: '#4CAF50', dashed: true },
        ],
      },
      {
        id: 'github-prs-semana',
        name: 'PRs Criados vs Mergeados',
        description: 'Volume semanal de Pull Requests',
        source: 'GitHub',
        category: 'Entrega',
        chartType: 'bar',
        legend: [
          { label: 'PRs Criados', color: '#FF5722' },
          { label: 'PRs Mergeados', color: '#4CAF50' },
        ],
      },
      {
        id: 'github-pr-time-breakdown',
        name: 'PR Time Breakdown',
        description: 'Distribuição de tempo entre etapas do PR',
        source: 'GitHub',
        category: 'Code Review',
        chartType: 'bar',
        legend: [
          { label: 'Abertura → Review', color: '#FF9800' },
          { label: 'Review → Merge', color: '#2196F3' },
        ],
      },
      {
        id: 'github-top-reviewers',
        name: 'Top Reviewers',
        description: 'Ranking dos revisores mais ativos',
        source: 'GitHub',
        category: 'Code Review',
        chartType: 'bar',
        legend: [
          { label: 'Reviews realizados', color: '#9C27B0' },
        ],
      },
      {
        id: 'github-distribuicao-comentarios',
        name: 'Distribuição de Comentários',
        description: 'Comentários por PR e por revisor',
        source: 'GitHub',
        category: 'Code Review',
        chartType: 'bar',
        legend: [
          { label: 'Comentários', color: '#00BCD4' },
        ],
      },
      {
        id: 'github-taxa-aprovacao',
        name: 'Taxa de Aprovação de PRs',
        description: 'Percentual de PRs aprovados sem mudanças solicitadas',
        source: 'GitHub',
        category: 'Qualidade',
        chartType: 'line',
        legend: [
          { label: 'Taxa de Aprovação (%)', color: '#4CAF50' },
        ],
      },
      {
        id: 'github-commits-autor',
        name: 'Commits por Autor',
        description: 'Volume de commits por desenvolvedor nos últimos 30 dias',
        source: 'GitHub',
        category: 'Produtividade',
        chartType: 'bar',
        legend: [
          { label: 'Commits', color: '#22d3ee' },
        ],
      },
      {
        id: 'github-issues-evolucao',
        name: 'Issues Abertas vs Fechadas',
        description: 'Evolução de issues abertas e resolvidas por semana',
        source: 'GitHub',
        category: 'Entrega',
        chartType: 'bar',
        legend: [
          { label: 'Abertas', color: '#F44336' },
          { label: 'Fechadas', color: '#4CAF50' },
        ],
      },
      {
        id: 'github-hotfix-rate',
        name: 'Hotfix Rate',
        description: 'Proporção de hotfixes em relação a releases planejadas',
        source: 'GitHub',
        category: 'Qualidade',
        chartType: 'line',
        legend: [
          { label: 'Hotfixes (%)', color: '#F44336' },
        ],
      },
      {
        id: 'github-branch-lifetime',
        name: 'Branch Lifetime',
        description: 'Tempo médio de vida das branches antes do merge',
        source: 'GitHub',
        category: 'Code Review',
        chartType: 'line',
        legend: [
          { label: 'Dias médios', color: '#607D8B' },
        ],
      },
      // Jira
      {
        id: 'jira-task-cycle-time',
        name: 'Task Cycle Time por Projeto',
        description: 'Tempo médio em dias do início até conclusão',
        source: 'Jira',
        category: 'Entrega',
        chartType: 'area',
        dynamicLegend: true,
      },
      {
        id: 'jira-velocidade-individual',
        name: 'Velocidade Individual e do Time',
        description: 'Métricas de produtividade dos últimos 30 dias',
        source: 'Jira',
        category: 'Produtividade',
        chartType: 'velocity-table',
      },
      {
        id: 'jira-lead-time',
        name: 'Lead Time',
        description: 'Tempo desde criação até conclusão da task',
        source: 'Jira',
        category: 'Entrega',
        chartType: 'line',
        legend: [
          { label: 'Lead Time (dias)', color: '#9C27B0' },
        ],
      },
      {
        id: 'jira-burndown',
        name: 'Burndown por Sprint',
        description: 'Progresso de conclusão ao longo do sprint',
        source: 'Jira',
        category: 'Entrega',
        chartType: 'line',
        legend: [
          { label: 'Ideal', color: '#2196F3', dashed: true },
          { label: 'Real', color: '#22d3ee' },
        ],
      },
      {
        id: 'jira-bugs-rate',
        name: 'Taxa de Bugs por Projeto',
        description: 'Percentual de tasks que são bugs por projeto',
        source: 'Jira',
        category: 'Qualidade',
        chartType: 'bar',
        legend: [
          { label: 'Bugs (%)', color: '#F44336' },
        ],
      },
      {
        id: 'jira-throughput',
        name: 'Throughput do Time',
        description: 'Número de tasks concluídas por semana',
        source: 'Jira',
        category: 'Produtividade',
        chartType: 'bar',
        legend: [
          { label: 'Tasks concluídas', color: '#4CAF50' },
        ],
      },
      // Azure Pipelines
      {
        id: 'azure-build-success',
        name: 'Taxa de Build Success',
        description: 'Percentual de builds bem-sucedidos por pipeline',
        source: 'Azure Pipelines',
        category: 'CI/CD',
        chartType: 'line',
        legend: [
          { label: 'Sucesso (%)', color: '#4CAF50' },
        ],
      },
      {
        id: 'azure-deploy-frequency',
        name: 'Frequência de Deploy',
        description: 'Número de deploys realizados por semana',
        source: 'Azure Pipelines',
        category: 'CI/CD',
        chartType: 'bar',
        legend: [
          { label: 'Deploys', color: '#2196F3' },
        ],
      },
      {
        id: 'azure-pipeline-duration',
        name: 'Duração Média de Pipeline',
        description: 'Tempo médio de execução do pipeline em minutos',
        source: 'Azure Pipelines',
        category: 'CI/CD',
        chartType: 'line',
        legend: [
          { label: 'Duração (min)', color: '#FF9800' },
        ],
      },
      // SonarQube
      {
        id: 'sonar-code-coverage',
        name: 'Cobertura de Código',
        description: 'Percentual de linhas de código cobertas por testes',
        source: 'SonarQube',
        category: 'Qualidade',
        chartType: 'line',
        legend: [
          { label: 'Cobertura (%)', color: '#4CAF50' },
        ],
      },
      {
        id: 'sonar-code-smells',
        name: 'Code Smells',
        description: 'Número de problemas de qualidade identificados',
        source: 'SonarQube',
        category: 'Qualidade',
        chartType: 'bar',
        legend: [
          { label: 'Code Smells', color: '#FF9800' },
        ],
      },
      {
        id: 'sonar-technical-debt',
        name: 'Dívida Técnica',
        description: 'Estimativa de tempo para corrigir problemas de código',
        source: 'SonarQube',
        category: 'Qualidade',
        chartType: 'line',
        legend: [
          { label: 'Horas estimadas', color: '#9C27B0' },
        ],
      },
    ],

    // Mock: dados da métrica Velocidade Individual e do Time
    mockVelocityData: {
      teamAvg: 87,
      tasksPerMonth: 24,
      prsPerMonth: 10,
      members: [
        { name: 'Ana Silva', score: 95, trend: 1, tasks: 28, prs: 12 },
        { name: 'Carlos Lima', score: 88, trend: 1, tasks: 24, prs: 10 },
        { name: 'Maria Santos', score: 82, trend: 0, tasks: 22, prs: 8 },
        { name: 'Pedro Costa', score: 76, trend: -1, tasks: 18, prs: 6 },
      ],
    },
    // Paleta de cores para legendas dinâmicas (projetos do Jira, etc.)
    dynamicColorPalette: [
      '#FF5722', '#2196F3', '#FF9800', '#4CAF50', '#9C27B0',
      '#00BCD4', '#E91E63', '#607D8B', '#795548', '#3F51B5',
    ],
  }),

  computed: {
    // Opções do filtro de time (dados reais da API, sem arquivados)
    teamFilterOptions() {
      return [
        { id: 'all', name: 'Todos os Times' },
        ...this.apiTeams,
      ]
    },

    // Opções do filtro de pessoa (dados reais da API, sem desativados, filtrado por time)
    userFilterOptions() {
      const allOption = { id: 'all', name: 'Todos os Colaboradores' }
      if (this.selectedTeamFilter === 'all') {
        return [allOption, ...this.apiUsers]
      }
      // Quando um time está selecionado, usa os membros carregados via TeamsService.getMembers
      return [allOption, ...this.teamFilteredUsers]
    },

    // Modo individual ativo
    isIndividualMode() {
      return this.selectedUserFilter !== 'all'
    },

    // Dados do usuário selecionado (API real ou fallback mock)
    selectedUserData() {
      if (!this.isIndividualMode) return {}

      let data = null

      // Priorizar dados reais da API
      if (this.individualAnalysisData && this.individualAnalysisData.userId === this.selectedUserFilter) {
        data = this.individualAnalysisData
      } else {
        // Fallback para mock enquanto carrega
        data = this.mockUsers.find(u => u.id === this.selectedUserFilter)
      }

      if (!data) return {}

      // Enriquecer miniCards com tooltips fixos quando o backend não envia
      const defaultMiniCardTooltips = {
        'Tasks': 'Volume de tasks concluídas no período. Comparação com a média do time para avaliar produtividade individual.',
        'PRs': 'Pull Requests criados e mergeados. Tamanho médio e frequência indicam boas práticas de entrega contínua.',
        'Reviews': 'Code reviews realizados. Participação ativa em reviews melhora a qualidade geral do time.',
        'Cycle': 'Cycle time médio do PR (abertura até merge). Quanto menor, mais eficiente o fluxo de entrega.',
      }

      if (data.miniCards) {
        data = {
          ...data,
          miniCards: data.miniCards.map(card => ({
            ...card,
            tooltip: card.tooltip || defaultMiniCardTooltips[card.label] || `Métrica ${card.label} do período selecionado.`,
          })),
        }
      }

      // Enriquecer dimensões com tooltips fixos quando o backend não envia
      const defaultDimensionTooltips = {
        'Entrega': 'Avalia consistência e volume de entregas. Considera tasks concluídas, PRs mergeados e cumprimento de prazos.',
        'Qualidade': 'Mede qualidade do código entregue. Considera taxa de bugs, PRs reabertos e cobertura de testes.',
        'Colaboração': 'Avalia participação em code reviews, mentoria e contribuições cross-team.',
        'Evolução': 'Mede crescimento técnico ao longo do tempo. Considera complexidade das tasks e absorção de novos desafios.',
        'Consistência': 'Avalia regularidade das entregas. Sem picos nem vales — padrão de profissional maduro.',
      }

      if (data.dimensoes) {
        data = {
          ...data,
          dimensoes: data.dimensoes.map(dim => ({
            ...dim,
            tooltip: dim.tooltip || defaultDimensionTooltips[dim.name] || `Dimensão ${dim.name} calculada com base nas métricas do período.`,
          })),
        }
      }

      return data
    },

    // Avatar do usuário selecionado (do sistema, não do GitHub)
    selectedUserAvatarUrl() {
      if (!this.isIndividualMode) return null
      // Buscar avatar do sistema (apiUsers ou teamFilteredUsers)
      const fromApi = this.apiUsers.find(u => u.id === this.selectedUserFilter)
      if (fromApi && fromApi.avatarUrl) return fromApi.avatarUrl
      const fromTeam = this.teamFilteredUsers.find(u => u.id === this.selectedUserFilter)
      if (fromTeam && fromTeam.avatarUrl) return fromTeam.avatarUrl
      return null
    },

    // Total de métricas favoritadas
    totalFavoriteMetrics() {
      return this.favoriteMetricIds.length
    },

    // Métricas favoritadas enriquecidas
    favoriteMetrics() {
      return this.allAvailableMetrics.filter(m => this.favoriteMetricIds.includes(m.id))
    },

    // Métricas favoritadas agrupadas por fonte
    favoriteMetricsGrouped() {
      const groups = {}
      this.favoriteMetrics.forEach(metric => {
        if (!groups[metric.source]) {
          groups[metric.source] = { source: metric.source, metrics: [] }
        }
        groups[metric.source].metrics.push(metric)
      })
      return Object.values(groups)
    },

    // Métricas do catálogo filtradas por busca + fonte + categoria
    filteredCatalogMetrics() {
      const search = (this.metricsCatalogSearch || '').toLowerCase()
      return this.allAvailableMetrics.filter(m => {
        const matchSource = this.metricsCatalogSourceFilter === 'Todos' || m.source === this.metricsCatalogSourceFilter
        const matchCategory = this.metricsCatalogCategoryFilter === 'Todas' || m.category === this.metricsCatalogCategoryFilter
        const matchSearch = !search || m.name.toLowerCase().includes(search) || m.description.toLowerCase().includes(search)
        return matchSource && matchCategory && matchSearch
      })
    },

    // Métricas filtradas do catálogo agrupadas por fonte
    filteredCatalogGrouped() {
      const groups = {}
      this.filteredCatalogMetrics.forEach(metric => {
        if (!groups[metric.source]) {
          groups[metric.source] = { source: metric.source, metrics: [] }
        }
        groups[metric.source].metrics.push(metric)
      })
      return Object.values(groups)
    },
  },

  async mounted() {
    await Promise.all([
      this.loadDashboardData(),
      this.loadFavoriteMetrics(),
    ])
    this.$nextTick(() => {
      if (this.viewMode === 'geral') {
        this.initVelocityChart()
      } else if (this.viewMode === 'engenharia') {
        this.initFavoriteCharts()
      }
    })
  },

  beforeDestroy() {
    if (this.velocityChart) {
      this.velocityChart.destroy()
    }
    if (this.radarChart) {
      this.radarChart.destroy()
    }
    if (this.individualRadarChartInstance) {
      this.individualRadarChartInstance.destroy()
    }
    // Destruir gráficos das métricas favoritas
    Object.values(this.favoriteChartInstances).forEach(chart => {
      if (chart) chart.destroy()
    })
  },

  watch: {
    viewMode(newMode) {
      if (newMode === 'engenharia') {
        this.loadEngineeringData()
      }
      this.$nextTick(() => {
        if (newMode === 'geral') {
          this.initVelocityChart()
        } else if (newMode === 'engenharia') {
          if (this.isIndividualMode) {
            this.initIndividualRadarChart()
          } else {
            this.initFavoriteCharts()
          }
        }
      })
    },

    favoriteMetricIds() {
      if (this.viewMode === 'engenharia') {
        this.$nextTick(() => {
          this.initFavoriteCharts()
        })
      }
    },
  },

  methods: {
    // === CARREGAR TIMES E USUÁRIOS ===
    async loadTeamsAndUsers() {
      try {
        const [teamsResponse, usersResponse] = await Promise.all([
          TeamsService.list(),
          UsersService.list(),
        ])

        // Filtrar times arquivados
        this.apiTeams = (teamsResponse.data || []).filter(
          t => t.status !== 'ARCHIVED'
        )

        // Filtrar usuários desativados
        this.apiUsers = (usersResponse.data || []).filter(
          u => u.status !== 'DISABLED'
        )

        console.log('✅ Times carregados:', this.apiTeams.length)
        console.log('✅ Usuários carregados:', this.apiUsers.length)
      } catch (error) {
        console.error('❌ Erro ao carregar times/usuários:', error)
      }
    },

    // === FILTROS TIME/PESSOA ===
    async onTeamFilterChange() {
      // Resetar seleção de pessoa ao trocar time
      this.selectedUserFilter = 'all'
      this.teamFilteredUsers = []
      this.individualAnalysisData = null

      if (this.selectedTeamFilter && this.selectedTeamFilter !== 'all') {
        this.loadingTeamUsers = true
        try {
          const response = await TeamsService.getMembers(this.selectedTeamFilter)
          // Normalizar campo id (backend pode retornar userId ou id)
          this.teamFilteredUsers = (response.data || []).map(member => ({
            ...member,
            id: member.id || member.userId,
          })).filter(u => u.status !== 'DISABLED')
          console.log('✅ Membros do time carregados:', this.teamFilteredUsers.length)
        } catch (error) {
          console.error('❌ Erro ao carregar membros do time:', error)
        } finally {
          this.loadingTeamUsers = false
        }
      }

      // Recarregar todos os dados respeitando o filtro de time
      this.loadDashboardData()
      if (this.viewMode === 'engenharia') {
        this.loadEngineeringData()
        this.loadAllFavoriteMetricData()
      }
    },

    async onUserFilterChange() {
      if (this.isIndividualMode) {
        // Carregar análise individual + recarregar dados filtrados pelo userId
        await this.loadIndividualAnalysis(this.selectedUserFilter)
        this.$nextTick(() => {
          this.initIndividualRadarChart()
        })
      } else {
        this.individualAnalysisData = null
      }

      // Recarregar todos os dados respeitando o filtro de usuário
      this.loadDashboardData()
      if (this.viewMode === 'engenharia') {
        this.loadEngineeringData()
        this.loadAllFavoriteMetricData()
      }
    },

    async loadIndividualAnalysis(userId) {
      this.loadingIndividual = true
      this.individualAnalysisData = null
      try {
        const params = this.getFilterParams()
        const response = await ManagerService.getIndividualAnalysis(userId, params)
        this.individualAnalysisData = response.data
        console.log('✅ Análise individual carregada:', this.individualAnalysisData.name)
      } catch (error) {
        console.error('❌ Erro ao carregar análise individual:', error)
        this.individualAnalysisData = null
      } finally {
        this.loadingIndividual = false
      }
    },

    // Inicializa o radar chart da análise individual
    initIndividualRadarChart() {
      this.$nextTick(() => {
        const canvas = this.$refs.individualRadarChart
        if (!canvas) return

        if (this.individualRadarChartInstance) {
          this.individualRadarChartInstance.destroy()
        }

        const userData = this.selectedUserData
        if (!userData || !userData.radar) return

        const radarLabels = ['Entrega', 'Qualidade', 'Colaboração', 'Evolução', 'Consistência']

        const ctx = canvas.getContext('2d')
        // Cores adaptativas para Radar
        const primaryColor = '#22d3ee'
        const primaryBgColor = 'rgba(34, 211, 238, 0.2)'
        const secondaryColor = '#BDBDBD'
        const secondaryBgColor = 'rgba(189, 189, 189, 0.15)'
        const gridColor = this.$vuetify.theme.dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
        const labelColor = this.$vuetify.theme.dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'

        this.individualRadarChartInstance = new Chart(ctx, {
          type: 'radar',
          data: {
            labels: radarLabels,
            datasets: [
              {
                label: userData.name,
                data: userData.radar.user,
                backgroundColor: primaryBgColor,
                borderColor: primaryColor,
                borderWidth: 2,
                pointBackgroundColor: primaryColor,
                pointBorderColor: primaryColor,
                pointRadius: 4,
              },
              {
                label: 'Média do Time',
                data: userData.radar.team,
                backgroundColor: secondaryBgColor,
                borderColor: secondaryColor,
                borderWidth: 2,
                borderDash: [5, 5],
                pointBackgroundColor: secondaryColor,
                pointBorderColor: secondaryColor,
                pointRadius: 3,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: false },
            scale: {
              ticks: {
                beginAtZero: true,
                max: 100,
                stepSize: 20,
                display: false,
              },
              pointLabels: {
                fontSize: 12,
                fontColor: labelColor,
                fontFamily: 'Inter, sans-serif'
              },
              gridLines: {
                color: gridColor,
              },
              angleLines: {
                color: gridColor
              }
            },
            tooltips: {
              callbacks: {
                label: (item, data) => {
                  return data.datasets[item.datasetIndex].label + ': ' + item.yLabel
                },
              },
            },
          },
        })
      })
    },

    // === FILTROS DE DATA ===
    applyQuickFilter() {
      if (!this.quickDateFilter) {
        this.clearDateFilter()
        return
      }
      
      const today = new Date()
      const startDate = new Date()
      startDate.setDate(today.getDate() - this.quickDateFilter)
      
      this.filterStartDate = startDate.toISOString().split('T')[0]
      this.filterEndDate = today.toISOString().split('T')[0]
      
      this.applyDateFilter()
    },
    
    applyDateFilter() {
      this.loadDashboardData()
      if (this.viewMode === 'engenharia') {
        this.loadEngineeringData()
        this.loadAllFavoriteMetricData()
      }
    },
    
    clearDateFilter() {
      this.filterStartDate = null
      this.filterEndDate = null
      this.quickDateFilter = null
      this.selectedTeamFilter = 'all'
      this.selectedUserFilter = 'all'
      this.teamFilteredUsers = []
      this.individualAnalysisData = null
      this.loadDashboardData()
      if (this.viewMode === 'engenharia') {
        this.loadEngineeringData()
        this.loadAllFavoriteMetricData()
      }
    },
    
    getDateParams() {
      const params = {}
      if (this.filterStartDate) {
        params.startDate = this.filterStartDate
      }
      if (this.filterEndDate) {
        params.endDate = this.filterEndDate
      }
      return params
    },

    /**
     * Retorna parâmetros completos de filtro: data + teamId + userId
     * Todos os endpoints DEVEM usar este método para respeitar os filtros aplicados.
     * Quando o backend recebe teamId → filtra dados apenas dos membros desse time
     * Quando recebe userId → filtra dados de um colaborador específico
     */
    getFilterParams() {
      const params = this.getDateParams()
      if (this.selectedTeamFilter && this.selectedTeamFilter !== 'all') {
        params.teamId = this.selectedTeamFilter
      }
      if (this.selectedUserFilter && this.selectedUserFilter !== 'all') {
        params.userId = this.selectedUserFilter
      }
      return params
    },

    // === CARREGAR DADOS - VISÃO GERAL ===
    async loadDashboardData() {
      this.loading = true
      try {
        await Promise.all([
          this.loadTeamsAndUsers(),
          this.loadPendingPDIs(),
          this.loadOverdueTasks(),
          this.loadUpcomingOneOnOnes(),
          this.loadProductivityStats(),
          this.loadCompetenciesStats(),
          this.loadFeedbacksStats(),
          this.loadOverdueEvaluations(),
          this.loadIntelligentAnalysis(),
          this.loadTeamVelocity(),
          this.loadJiraMetrics(),
        ])
      } finally {
        this.loading = false
        this.$nextTick(() => {
          if (this.viewMode === 'geral') {
            this.initVelocityChart()
          }
        })
      }
    },

    // === CARREGAR DADOS - VISÃO ENGENHARIA ===
    async loadEngineeringData() {
      this.loadingEngineering = true
      try {
        await Promise.all([
          this.loadEngineeringMetrics(),
          this.loadEngineeringCompetencies(),
        ])
      } finally {
        this.loadingEngineering = false
        this.$nextTick(() => {
          this.initFavoriteCharts()
        })
      }
    },

    async loadPendingPDIs() {
      try {
        const params = this.getFilterParams()
        const response = await ManagerService.getPendingPDIs(params)
        const data = response.data
        this.stats.pendingPDIs = data.count || 0
        this.stats.pendingPDIsTrend = data.trend || ''
        this.pendingPDIs = data.items || []
      } catch (error) {
        console.error('Erro ao carregar PDIs pendentes:', error)
      }
    },

    async loadOverdueTasks() {
      try {
        const params = this.getFilterParams()
        const response = await ManagerService.getOverdueTasks(params)
        const data = response.data
        this.stats.overdueTasks = data.count || 0
        this.stats.overdueTasksTrend = data.trend || ''
      } catch (error) {
        console.error('Erro ao carregar tarefas atrasadas:', error)
      }
    },

    async loadUpcomingOneOnOnes() {
      try {
        const params = this.getFilterParams()
        const response = await ManagerService.getUpcomingOneOnOnes(params)
        const data = response.data
        this.stats.upcomingOneOnOnes = data.count || 0
        this.stats.oneOnOnePercentage = data.percentageImprovement || 0
        this.stats.oneOnOneMonths = data.improvementMonths || 0
        this.nextOneOnOnes = data.items || []
      } catch (error) {
        console.error('Erro ao carregar reuniões 1:1:', error)
      }
    },

    async loadProductivityStats() {
      try {
        const params = this.getFilterParams()
        const response = await ManagerService.getProductivityStats(params)
        this.productivity = response.data || this.productivity
      } catch (error) {
        console.error('Erro ao carregar estatísticas de produtividade:', error)
      }
    },

    async loadCompetenciesStats() {
      try {
        const params = this.getFilterParams()
        const response = await ManagerService.getCompetenciesStats(params)
        const data = response.data
        this.stats.competencias = data.count || 0
      } catch (error) {
        console.error('Erro ao carregar competências:', error)
      }
    },

    async loadFeedbacksStats() {
      try {
        const params = this.getFilterParams()
        const response = await ManagerService.getFeedbacksStats(params)
        const data = response.data
        this.stats.feedbacks = data.count || 0
        this.stats.feedbacksTrend = data.trend || ''
      } catch (error) {
        console.error('Erro ao carregar feedbacks:', error)
      }
    },

    async loadOverdueEvaluations() {
      try {
        const params = this.getFilterParams()
        const response = await ManagerService.getOverdueEvaluations(params)
        this.overdueEvaluations = response.data || []
        this.stats.overdueEvaluations = this.overdueEvaluations.length
      } catch (error) {
        console.error('Erro ao carregar avaliações atrasadas:', error)
        this.stats.overdueEvaluations = 0
      }
    },

    async loadIntelligentAnalysis() {
      // Busca insights já existentes — chamada rápida (GET)
      this.intelligentAnalysis.loading = true
      try {
        const params = { ...this.getFilterParams(), limit: 5 }
        const response = await ManagerService.getAiInsights(params)
        const data = response.data
        const list = (data.insights || data || []).sort(() => Math.random() - 0.5)
        this.intelligentAnalysis.insights = list
        this.intelligentAnalysis.total = data.total || list.length
        this.intelligentAnalysis.generatedAt = data.generatedAt || null
        this.intelligentAnalysis.currentIndex = 0
        console.log('✅ Insights de IA carregados (cache):', this.intelligentAnalysis.total)
      } catch (error) {
        console.error('Erro ao carregar insights de IA:', error)
      } finally {
        this.intelligentAnalysis.loading = false
      }
    },

    async regenerateIntelligentAnalysis() {
      // Solicita nova geração via IA — chamada lenta (POST), acionada pelo botão de refresh
      this.intelligentAnalysis.generating = true
      try {
        const body = this.getFilterParams()
        const response = await ManagerService.generateAiInsights(body)
        const data = response.data
        const list = (data.insights || []).sort(() => Math.random() - 0.5)
        this.intelligentAnalysis.insights = list
        this.intelligentAnalysis.total = data.total || list.length
        this.intelligentAnalysis.generatedAt = data.generatedAt || null
        this.intelligentAnalysis.currentIndex = 0
        console.log('✅ Insights de IA regenerados:', this.intelligentAnalysis.total)
      } catch (error) {
        console.error('Erro ao regenerar insights de IA:', error)
      } finally {
        this.intelligentAnalysis.generating = false
      }
    },

    async loadJiraMetrics() {
      try {
        this.jiraMetrics.loading = true
        const tenantId = this.$store.state.user?.tenantId || localStorage.getItem('tenantId')
        
        if (!tenantId) {
          console.warn('TenantId não encontrado para carregar métricas')
          return
        }

        // Verificar se a integração está conectada
        try {
          const statusResponse = await IntegrationsService.getJiraStatus(tenantId)
          if (!statusResponse.data || !statusResponse.data.connected) {
            console.log('Integração não conectada')
            this.jiraMetrics.connected = false
            return
          }
        } catch (error) {
          console.log('Integração não disponível')
          this.jiraMetrics.connected = false
          return
        }

        // Parâmetros de filtro (time/usuário/data)
        const filterParams = this.getFilterParams()

        // Buscar resumo e métricas da equipe em paralelo
        const [summaryResponse, teamResponse] = await Promise.all([
          JiraService.getSummary(tenantId, filterParams).catch(() => ({ data: null })),
          JiraService.getTeamMetrics(tenantId, filterParams).catch(() => ({ data: [] }))
        ])

        // Processar resumo
        if (summaryResponse.data) {
          this.jiraMetrics.summary = summaryResponse.data
          this.jiraMetrics.connected = true
        }

        // Processar métricas da equipe (filtrar apenas usuários com métricas válidas)
        const validTeamMetrics = (teamResponse.data || []).filter(user => 
          user.metrics && !user.error
        )
        
        this.jiraMetrics.team = validTeamMetrics

        // Calcular top 3 performers (baseado em issues concluídos últimos 30 dias)
        this.jiraMetrics.topPerformers = [...validTeamMetrics]
          .sort((a, b) => 
            (b.metrics.doneLast30Days || 0) - (a.metrics.doneLast30Days || 0)
          )
          .slice(0, 3)

        console.log('✅ Métricas de desenvolvimento carregadas:', {
          summary: this.jiraMetrics.summary,
          teamCount: this.jiraMetrics.team.length,
          topPerformers: this.jiraMetrics.topPerformers.length
        })

        // Pre-carregar métricas avançadas em background para que
        // já estejam cacheadas quando o usuário clicar em "Ver Métricas Avançadas"
        this.startJiraAdvancedBackgroundCache()

      } catch (error) {
        console.error('Erro ao carregar métricas:', error)
        this.jiraMetrics.connected = false
      } finally {
        this.jiraMetrics.loading = false
      }
    },

    // ============================================================
    // CACHE HELPERS — MÉTRICAS AVANÇADAS JIRA
    // ============================================================
    _getJiraCacheKey() {
      const tenantId = this.$store.state.user?.tenantId || localStorage.getItem('tenantId')
      // Incluir filtros na chave para não servir cache de outro escopo
      const teamPart = (this.selectedTeamFilter && this.selectedTeamFilter !== 'all') ? `_t${this.selectedTeamFilter}` : ''
      const userPart = (this.selectedUserFilter && this.selectedUserFilter !== 'all') ? `_u${this.selectedUserFilter}` : ''
      return `jira_advanced_cache_${tenantId}${teamPart}${userPart}`
    },

    _loadAdvancedFromCache() {
      try {
        const raw = localStorage.getItem(this._getJiraCacheKey())
        if (!raw) return false
        const { data, timestamp } = JSON.parse(raw)
        const age = Date.now() - timestamp
        if (age > 12 * 60 * 60 * 1000) return false // expirou (12h)
        // Restaurar dados do cache
        if (data.leadTime) this.jiraMetrics.advanced.leadTime = data.leadTime
        if (data.cycleTime) this.jiraMetrics.advanced.cycleTime = data.cycleTime
        if (data.velocity) this.jiraMetrics.advanced.velocity = data.velocity
        if (data.issuesCreated) this.jiraMetrics.advanced.issuesCreated = data.issuesCreated
        if (data.issuesReopened) this.jiraMetrics.advanced.issuesReopened = data.issuesReopened
        if (data.comments) this.jiraMetrics.advanced.comments = data.comments
        if (data.projects) this.jiraMetrics.advanced.projects = data.projects
        this.jiraMetrics.advancedCachedAt = timestamp
        this.jiraMetrics.tabDataLoaded = { timing: true, velocity: true, projects: true }
        return true
      } catch (e) {
        return false
      }
    },

    _saveAdvancedToCache() {
      try {
        const payload = {
          timestamp: Date.now(),
          data: {
            leadTime: this.jiraMetrics.advanced.leadTime,
            cycleTime: this.jiraMetrics.advanced.cycleTime,
            velocity: this.jiraMetrics.advanced.velocity,
            issuesCreated: this.jiraMetrics.advanced.issuesCreated,
            issuesReopened: this.jiraMetrics.advanced.issuesReopened,
            comments: this.jiraMetrics.advanced.comments,
            projects: this.jiraMetrics.advanced.projects
          }
        }
        localStorage.setItem(this._getJiraCacheKey(), JSON.stringify(payload))
        this.jiraMetrics.advancedCachedAt = payload.timestamp
      } catch (e) {
        console.warn('Não foi possível salvar cache avançado:', e)
      }
    },

    getAdvancedCacheAge() {
      if (!this.jiraMetrics.advancedCachedAt) return null
      const minutes = Math.floor((Date.now() - this.jiraMetrics.advancedCachedAt) / 60000)
      if (minutes < 1) return 'agora mesmo'
      if (minutes < 60) return `há ${minutes} min`
      const hours = Math.floor(minutes / 60)
      return `há ${hours}h`
    },

    async forceSyncAdvanced() {
      try {
        localStorage.removeItem(this._getJiraCacheKey())
      } catch (e) { /* ignore */ }
      this.jiraMetrics.advancedCachedAt = null
      this.jiraMetrics.tabDataLoaded = { timing: false, velocity: false, projects: false }
      await this.loadJiraAdvancedMetrics(true)
    },

    // Pre-carregamento silencioso em background após conectar
    startJiraAdvancedBackgroundCache() {
      // Só pre-carrega se ainda não há cache válido
      if (!this._loadAdvancedFromCache()) {
        setTimeout(() => {
          this.loadJiraAdvancedMetrics(false, true /* silent */)
        }, 2000) // aguarda 2s para não competir com o carregamento principal
      }
    },

    async loadJiraAdvancedMetrics(forceRefresh = false, silent = false) {
      // 1. Tentar cache se não for força-refresh
      if (!forceRefresh && this._loadAdvancedFromCache()) {
        if (!silent) console.log('✅ Métricas avançadas carregadas do cache local')
        return
      }

      if (!silent) {
        this.jiraMetrics.loadingAdvanced = true
      }

      const tenantId = this.$store.state.user?.tenantId || localStorage.getItem('tenantId')
      if (!tenantId) return

      // Parâmetros de filtro (time/usuário/data)
      const filterParams = this.getFilterParams()

      // 2. Disparar cada chamada de forma INDEPENDENTE para renderização progressiva
      //    Os cards aparecem à medida que cada resposta chega

      const calls = [
        {
          key: 'leadTime',
          fn: () => JiraService.getLeadTime(tenantId, filterParams),
          onSuccess: (data) => { this.jiraMetrics.advanced.leadTime = data }
        },
        {
          key: 'cycleTime',
          fn: () => JiraService.getCycleTime(tenantId, filterParams),
          onSuccess: (data) => { this.jiraMetrics.advanced.cycleTime = data }
        },
        {
          key: 'velocity',
          fn: () => JiraService.getVelocity(tenantId, filterParams),
          onSuccess: (data) => { this.jiraMetrics.advanced.velocity = data }
        },
        {
          key: 'created',
          fn: () => JiraService.getIssuesCreated(tenantId, filterParams),
          onSuccess: (data) => { this.jiraMetrics.advanced.issuesCreated = data }
        },
        {
          key: 'reopened',
          fn: () => JiraService.getIssuesReopened(tenantId, filterParams),
          onSuccess: (data) => { this.jiraMetrics.advanced.issuesReopened = data }
        },
        {
          key: 'comments',
          fn: () => JiraService.getComments(tenantId, filterParams),
          onSuccess: (data) => { this.jiraMetrics.advanced.comments = data }
        },
        {
          key: 'projects',
          fn: () => JiraService.getIssuesByProject(tenantId, filterParams),
          onSuccess: (data) => { this.jiraMetrics.advanced.projects = data }
        }
      ]

      // Marcar todas as seções como loading
      calls.forEach(c => { this.jiraMetrics.sectionLoading[c.key] = true })

      try {
        // Disparar todas em paralelo, mas processar cada uma ao resolver
        await Promise.allSettled(
          calls.map(async (call) => {
            try {
              const res = await call.fn()
              if (res.data) {
                call.onSuccess(res.data)
                // Marcar tab como carregada conforme seção completa
                if (['leadTime', 'cycleTime', 'reopened'].includes(call.key)) {
                  const timingDone = !this.jiraMetrics.sectionLoading.leadTime &&
                                     !this.jiraMetrics.sectionLoading.cycleTime
                  if (timingDone) this.jiraMetrics.tabDataLoaded.timing = true
                }
                if (call.key === 'velocity') this.jiraMetrics.tabDataLoaded.velocity = true
                if (call.key === 'projects') this.jiraMetrics.tabDataLoaded.projects = true
              }
            } catch (err) {
              console.warn(`Erro ao carregar ${call.key}:`, err)
            } finally {
              this.jiraMetrics.sectionLoading[call.key] = false
            }
          })
        )

        // Marcar todos tabs como carregados e salvar cache
        this.jiraMetrics.tabDataLoaded = { timing: true, velocity: true, projects: true }
        this._saveAdvancedToCache()

        console.log('✅ Métricas avançadas carregadas e cacheadas')
      } catch (error) {
        console.error('Erro ao carregar métricas avançadas:', error)
      } finally {
        this.jiraMetrics.loadingAdvanced = false
      }
    },

    toggleJiraAdvanced() {
      this.jiraMetrics.showAdvanced = !this.jiraMetrics.showAdvanced

      if (this.jiraMetrics.showAdvanced) {
        // Tenta cache primeiro; só carrega se necessário
        const hasValidCache = this._loadAdvancedFromCache()
        if (!hasValidCache) {
          this.loadJiraAdvancedMetrics()
        }
      }
    },

    // === MÉTODOS AUXILIARES - JIRA MÉTRICAS AVANÇADAS ===
    getLeadTimeColor(days) {
      if (days <= 3) return 'green lighten-4'
      if (days <= 5) return 'yellow lighten-4'
      return 'red lighten-4'
    },

    getCycleTimeColor(days) {
      if (days <= 2) return 'green lighten-4'
      if (days <= 4) return 'yellow lighten-4'
      return 'red lighten-4'
    },

    getCycleTime(userId) {
      const user = this.jiraMetrics.advanced.cycleTime.userCycleTimes.find(u => u.userId === userId)
      return user ? user.cycleTimeAvgDays : 0
    },

    getReopened(userId) {
      const user = this.jiraMetrics.advanced.issuesReopened.userMetrics.find(u => u.userId === userId)
      return user ? user.issuesReopened : 0
    },

    getProjectPercentage(issueCount) {
      const total = this.jiraMetrics.advanced.projects.projects.reduce((sum, p) => sum + p.issueCount, 0)
      return (issueCount / Math.max(total, 1)) * 100
    },

    async loadTeamVelocity() {
      try {
        const params = this.getFilterParams()
        const response = await ManagerService.getTeamVelocity(params)
        const data = response.data
        this.velocityData = {
          weeks: data.weeks || [],
          tasks: data.tasks || [],
          meta: data.meta || 0
        }
      } catch (error) {
        console.error('Erro ao carregar velocidade do time:', error)
      }
    },

    async loadEngineeringMetrics() {
      try {
        const params = this.getFilterParams()
        const response = await ManagerService.getEngineeringMetrics(params)
        const data = response.data
        this.engineeringMetrics = {
          entrega: data.entrega || this.engineeringMetrics.entrega,
          qualidade: data.qualidade || this.engineeringMetrics.qualidade,
          colaboracao: data.colaboracao || this.engineeringMetrics.colaboracao,
          evolucaoTecnica: data.evolucaoTecnica || this.engineeringMetrics.evolucaoTecnica,
        }
      } catch (error) {
        console.error('Erro ao carregar métricas de engenharia:', error)
      }
    },

    async loadEngineeringCompetencies() {
      try {
        const params = this.getFilterParams()
        const response = await ManagerService.getEngineeringCompetencies(params)
        const data = response.data
        this.engineeringCompetencies = {
          categories: data.categories || [],
          currentPeriod: data.currentPeriod || [],
          previousPeriod: data.previousPeriod || []
        }
      } catch (error) {
        console.error('Erro ao carregar competências de engenharia:', error)
      }
    },

    // === HELPERS ===
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '-'
      const date = new Date(dateTimeString)
      const dateStr = date.toLocaleDateString('pt-BR')
      const timeStr = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      return `${dateStr.split('/')[0]}/${dateStr.split('/')[1]} ${timeStr}`
    },

    // === NAVEGAÇÃO ===
    goToPDIs() {
      this.$router.push('/approval')
    },

    goToOneOnOnes() {
      this.$router.push('/one-on-ones')
    },

    goToSkills() {
      this.$router.push('/skills')
    },

    goToFeedbacks() {
      this.$router.push('/feedbacks')
    },

    // === ANÁLISE INTELIGENTE - AÇÕES ===
    gerarFeedback() {
      if (!this.canPerformAction('gerar feedback')) return
      console.log('Gerar Feedback baseado na análise')
      this.$router.push('/dashboard/feedbacks')
    },

    criarPDI() {
      if (!this.canPerformAction('criar PDI')) return
      console.log('Criar PDI baseado na análise')
      this.$router.push('/dashboard/pdis')
    },

    prepararOneOnOne() {
      if (!this.canPerformAction('preparar 1:1')) return
      console.log('Preparar 1:1 baseado na análise')
      this.$router.push('/dashboard/one-on-ones')
    },

    // === GRÁFICO DE VELOCIDADE ===
    initVelocityChart() {
      if (!this.$refs.velocityChart) return

      const ctx = this.$refs.velocityChart.getContext('2d')
      
      const primaryColor = '#4ade80'
      const warningColor = '#FF9800'
      const gridColor = this.$vuetify.theme.dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
      const labelColor = this.$vuetify.theme.dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'

      // Determinar cor de cada barra (verde se >= meta, laranja se < meta)
      const barColors = this.velocityData.tasks.map(tasks =>
        tasks >= this.velocityData.meta ? primaryColor : warningColor
      )

      // Destruir gráfico anterior se existir
      if (this.velocityChart) {
        this.velocityChart.destroy()
      }

      this.velocityChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.velocityData.weeks,
          datasets: [{
            label: 'Tarefas Concluídas',
            data: this.velocityData.tasks,
            backgroundColor: barColors,
            borderColor: 'transparent',
            borderWidth: 0,
            barThickness: 40, // Deixei mais moderno e fino
            borderRadius: 6, // Exige Chart.js versão mais nova, se não tiver, ignora graciosamente
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false
          },
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true,
                stepSize: 7,
                max: 28,
                fontColor: labelColor,
                callback: function(value) {
                  return value
                }
              },
              gridLines: {
                display: true,
                color: gridColor,
                drawBorder: false,
              }
            }],
            xAxes: [{
              ticks: {
                fontColor: labelColor,
              },
              gridLines: {
                display: false
              }
            }]
          },
          tooltips: {
            backgroundColor: this.$vuetify.theme.dark ? '#1D1D1F' : '#FFFFFF',
            titleFontColor: this.$vuetify.theme.dark ? '#FFF' : '#000',
            bodyFontColor: this.$vuetify.theme.dark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
            borderColor: this.$vuetify.theme.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            borderWidth: 1,
            callbacks: {
              label: function(tooltipItem) {
                const value = tooltipItem.yLabel
                const meta = 21
                const status = value >= meta ? 'Meta atingida' : 'Abaixo da meta'
                return `${value} tarefas (${status})`
              }
            }
          }
        }
      })
    },

    // === GRÁFICO RADAR DE COMPETÊNCIAS (ENGENHARIA) ===
    initRadarChart() {
      // Metodo mantido pro caso de reuso, mas na refatoração visual eu limpei as chamadas diretas que não estão na tela nova. 
      // Não foi removido do script para garantir nenhuma quebra de lógica.
    },

    getAvatarColor(name) {
      if (!name) return '#22d3ee'
      const colors = ['#22d3ee', '#2196F3', '#FF9800', '#9C27B0', '#00BCD4', '#E91E63', '#607D8B', '#795548']
      const index = name.charCodeAt(0) % colors.length
      return colors[index]
    },

    // === HELPERS - INSIGHT TYPES (IA) ===
    getInsightTypeColor(type) {
      const map = {
        GROWTH_TREND:       '#4CAF50',
        CHURN_RISK:         '#F44336',
        USAGE_ANOMALY:      '#FF9800',
        ENGAGEMENT_PATTERN: '#2196F3',
        PERFORMANCE_INSIGHT:'#22d3ee',
        RECOMMENDATION:     '#9C27B0',
      }
      return map[type] || '#607D8B'
    },

    getInsightTypeBg(type) {
      // Adaptado pro CSS Variables no style
      return this.getInsightTypeColor(type)
    },

    getInsightTypeIcon(type) {
      const map = {
        GROWTH_TREND:       'mdi-trending-up',
        CHURN_RISK:         'mdi-account-off',
        USAGE_ANOMALY:      'mdi-chart-bell-curve',
        ENGAGEMENT_PATTERN: 'mdi-account-group',
        PERFORMANCE_INSIGHT:'mdi-lightning-bolt',
        RECOMMENDATION:     'mdi-lightbulb',
      }
      return map[type] || 'mdi-information-outline'
    },

    getInsightTypeLabel(type) {
      const map = {
        GROWTH_TREND:       'Tendência de Crescimento',
        CHURN_RISK:         'Risco de Churn',
        USAGE_ANOMALY:      'Anomalia de Uso',
        ENGAGEMENT_PATTERN: 'Padrão de Engajamento',
        PERFORMANCE_INSIGHT:'Performance',
        RECOMMENDATION:     'Recomendação',
      }
      return map[type] || type
    },

    getInitials(name) {
      if (!name) return '?'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    // === MÉTRICAS FAVORITAS ===

    isMetricFavorite(metricId) {
      return this.favoriteMetricIds.includes(metricId)
    },

    toggleFavoriteMetric(metricId) {
      const index = this.favoriteMetricIds.indexOf(metricId)
      if (index === -1) {
        this.favoriteMetricIds = [...this.favoriteMetricIds, metricId]
        // Carregar dados do back para a nova métrica e renderizar
        this.loadFavoriteMetricData(metricId).then(() => this.initFavoriteCharts())
      } else {
        this.favoriteMetricIds = this.favoriteMetricIds.filter(id => id !== metricId)
        // Destruir gráfico se existir
        if (this.favoriteChartInstances[metricId]) {
          this.favoriteChartInstances[metricId].destroy()
          this.$delete(this.favoriteChartInstances, metricId)
        }
        this.$delete(this.favoriteMetricsData, metricId)
      }
      // Persistir no backend
      this.saveFavoriteMetrics()
    },

    /**
     * Carrega métricas favoritas do backend
     */
    async loadFavoriteMetrics() {
      this.loadingFavorites = true
      try {
        const response = await ManagerService.getFavoriteMetrics()
        const ids = response.data?.metricIds || []
        // Filtrar apenas IDs que existem no catálogo local
        this.favoriteMetricIds = ids.filter(id =>
          this.allAvailableMetrics.some(m => m.id === id)
        )
        console.log('✅ Métricas favoritas carregadas:', this.favoriteMetricIds.length)
        // Carregar dados reais do back para cada métrica
        if (this.favoriteMetricIds.length > 0) {
          await this.loadAllFavoriteMetricData()
        }
      } catch (error) {
        console.error('❌ Erro ao carregar métricas favoritas:', error)
      } finally {
        this.loadingFavorites = false
      }
    },

    /**
     * Salva métricas favoritas no backend
     */
    async saveFavoriteMetrics() {
      try {
        await ManagerService.saveFavoriteMetrics(this.favoriteMetricIds)
        console.log('✅ Métricas favoritas salvas:', this.favoriteMetricIds.length)
      } catch (error) {
        console.error('❌ Erro ao salvar métricas favoritas:', error)
        this.$store.commit('SET_SNACKBAR', {
          show: true,
          color: 'error',
          text: 'Erro ao salvar métricas favoritas. Tente novamente.',
        })
      }
    },

    /**
     * Carrega dados do back para todas as métricas favoritas em paralelo
     */
    async loadAllFavoriteMetricData() {
      const params = this.getFilterParams()
      await Promise.all(
        this.favoriteMetricIds.map(id => this.loadFavoriteMetricData(id, params))
      )
      this.$nextTick(() => this.initFavoriteCharts())
    },

    /**
     * Carrega dados do back para uma métrica específica
     */
    async loadFavoriteMetricData(metricId, params = {}) {
      try {
        const response = await ManagerService.getMetricData(metricId, params)
        const apiData = response.data
        this.$set(this.favoriteMetricsData, metricId, apiData)

        // Para tabela de velocidade individual, atualizar dados da tabela
        if (metricId === 'jira-velocidade-individual' && apiData) {
          this.mockVelocityData = {
            teamAvg: apiData.teamAvg ?? this.mockVelocityData.teamAvg,
            tasksPerMonth: apiData.tasksPerMonth ?? this.mockVelocityData.tasksPerMonth,
            prsPerMonth: apiData.prsPerMonth ?? this.mockVelocityData.prsPerMonth,
            members: apiData.members || this.mockVelocityData.members,
          }
        }
      } catch (error) {
        console.warn(`⚠️ Sem dados do back para métrica ${metricId}, usando mock`)
      }
    },

    /**
     * Retorna a legenda de uma métrica.
     * Para métricas com dynamicLegend, gera a partir dos dados reais da API.
     */
    getMetricLegend(metric) {
      // Se tem dados reais e é dynamicLegend, gerar a partir dos datasets
      if (metric.dynamicLegend) {
        const apiData = this.favoriteMetricsData[metric.id]
        if (apiData && apiData.datasets) {
          return apiData.datasets.map((ds, i) => ({
            label: ds.label,
            color: this.dynamicColorPalette[i % this.dynamicColorPalette.length],
          }))
        }
        return []
      }
      return metric.legend || []
    },

    buildChartConfig(metric, apiData) {
      if (!apiData || !apiData.labels || !apiData.datasets) return null

      // Mapa de unidade no eixo Y por metricId
      const unitMap = {
        'github-pr-cycle-time': 'h',
        'github-pr-time-breakdown': 'h',
        'github-taxa-aprovacao': '%',
        'github-hotfix-rate': '%',
        'github-branch-lifetime': 'd',
        'jira-task-cycle-time': 'd',
        'jira-lead-time': 'd',
        'jira-burndown': '%',
        'jira-bugs-rate': '%',
        'azure-build-success': '%',
        'azure-pipeline-duration': 'min',
        'sonar-code-coverage': '%',
        'sonar-technical-debt': 'h',
      }

      const stackedIds = ['github-pr-time-breakdown']
      const areaIds = [
        'github-taxa-aprovacao', 'github-hotfix-rate',
        'jira-task-cycle-time', 'jira-lead-time', 'jira-burndown',
        'sonar-code-coverage', 'sonar-technical-debt',
      ]
      const percentMinMaxIds = ['github-taxa-aprovacao', 'azure-build-success', 'sonar-code-coverage']

      const unit = unitMap[metric.id] || ''
      const isStacked = stackedIds.includes(metric.id)
      const isArea = areaIds.includes(metric.id)
      const isLine = metric.chartType === 'line' || metric.chartType === 'area' || isArea

      // Cores pro chart js responsivo
      const gridColor = this.$vuetify.theme.dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
      const labelColor = this.$vuetify.theme.dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'

      // Mapa de cores: usar legend estática ou gerar dinâmicamente
      const colorMap = {}
      const resolvedLegend = this.getMetricLegend(metric)
      resolvedLegend.forEach(l => { colorMap[l.label] = l })

      const datasets = apiData.datasets.map((ds) => {
        const legendEntry = colorMap[ds.label] || {}
        const color = legendEntry.color || '#22d3ee'
        const dashed = legendEntry.dashed || false

        if (isLine) {
          return {
            label: ds.label,
            data: ds.data,
            borderColor: color,
            backgroundColor: isArea ? (color + '15') : 'transparent',
            borderWidth: 2,
            fill: isArea,
            pointRadius: dashed ? 0 : 4,
            pointBackgroundColor: color,
            ...(dashed ? { borderDash: [5, 5] } : {}),
          }
        } else {
          return {
            label: ds.label,
            data: ds.data,
            backgroundColor: color,
            borderRadius: 4
          }
        }
      })

      const commonOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            gridLines: { display: false },
            ticks: { fontSize: 10, fontColor: labelColor },
            ...(isStacked ? { stacked: true } : {}),
          }],
          yAxes: [{
            gridLines: { color: gridColor, drawBorder: false },
            ticks: {
              fontSize: 10,
              fontColor: labelColor,
              ...(unit ? { callback: v => v + unit } : {}),
              ...(percentMinMaxIds.includes(metric.id) ? { min: 0, max: 100 } : {}),
            },
            ...(isStacked ? { stacked: true } : {}),
          }],
        },
        tooltips: { 
          mode: 'index', 
          intersect: false,
          backgroundColor: this.$vuetify.theme.dark ? '#1D1D1F' : '#FFFFFF',
          titleFontColor: this.$vuetify.theme.dark ? '#FFF' : '#000',
          bodyFontColor: this.$vuetify.theme.dark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
          borderColor: this.$vuetify.theme.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          borderWidth: 1,
        },
      }

      return {
        type: isLine ? 'line' : metric.chartType,
        data: { labels: apiData.labels, datasets },
        options: commonOptions,
      }
    },

    getSourceColor(source) {
      const colors = {
        'GitHub': '#6e5494', // Roxo suave
        'Jira': '#2684FF',   // Azul Jira
        'Azure Pipelines': '#0078D7',
        'SonarQube': '#CB202D',
      }
      return colors[source] || '#22d3ee'
    },

    getSourceIcon(source) {
      const icons = {
        'GitHub': 'mdi-github',
        'Jira': 'mdi-jira',
        'Azure Pipelines': 'mdi-microsoft-azure-devops',
        'SonarQube': 'mdi-bug-check-outline',
      }
      return icons[source] || 'mdi-chart-line'
    },

    getCategoryColor(category) {
      const colors = {
        'Code Review': '#9C27B0',
        'Entrega': '#2196F3',
        'Qualidade': '#4CAF50',
        'CI/CD': '#FF9800',
        'Produtividade': '#607D8B',
      }
      return colors[category] || 'grey'
    },

    // Retorna os dados mock de Chart.js para cada métrica
    getMockChartConfig(metric) {
      const weeks = ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4', 'Sem 5', 'Sem 6']
      
      const gridColor = this.$vuetify.theme.dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
      const labelColor = this.$vuetify.theme.dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'

      const commonOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { fontSize: 10, fontColor: labelColor } }],
          yAxes: [{ gridLines: { color: gridColor, drawBorder: false }, ticks: { fontSize: 10, fontColor: labelColor } }],
        },
        tooltips: { 
          mode: 'index', 
          intersect: false,
          backgroundColor: this.$vuetify.theme.dark ? '#1D1D1F' : '#FFFFFF',
          titleFontColor: this.$vuetify.theme.dark ? '#FFF' : '#000',
          bodyFontColor: this.$vuetify.theme.dark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
          borderColor: this.$vuetify.theme.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          borderWidth: 1,
        },
      }

      const configs = {
        'github-pr-cycle-time': {
          type: 'line',
          data: {
            labels: weeks,
            datasets: [
              { label: 'Frontend', data: [4, 4.2, 3.9, 3.5, 3.3, 3.2], borderColor: '#FF5722', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 4, pointBackgroundColor: '#FF5722' },
              { label: 'Backend', data: [6, 5.5, 5, 4.8, 4.5, 4.2], borderColor: '#2196F3', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 4, pointBackgroundColor: '#2196F3' },
              { label: 'Mobile', data: [4.1, 4.3, 3.8, 3.3, 3.0, 2.9], borderColor: '#FF9800', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 4, pointBackgroundColor: '#FF9800' },
              { label: 'Média Geral', data: [4.5, 4.2, 4.0, 3.8, 3.5, 3.4], borderColor: '#4CAF50', backgroundColor: 'transparent', borderWidth: 2, borderDash: [5, 5], pointRadius: 4, pointBackgroundColor: '#4CAF50' },
            ],
          },
          options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { ...commonOptions.scales.yAxes[0].ticks, callback: v => v + 'h' } }] } },
        },
        'github-prs-semana': {
          type: 'bar',
          data: {
            labels: weeks,
            datasets: [
              { label: 'PRs Criados', data: [18, 22, 25, 20, 29, 26], backgroundColor: '#FF5722', borderRadius: 4 },
              { label: 'PRs Mergeados', data: [15, 20, 23, 22, 25, 27], backgroundColor: '#4CAF50', borderRadius: 4 },
            ],
          },
          options: commonOptions,
        },
        'github-pr-time-breakdown': {
          type: 'bar',
          data: {
            labels: weeks,
            datasets: [
              { label: 'Abertura → Review', data: [2.5, 2.2, 2.0, 1.8, 1.7, 1.5], backgroundColor: '#FF9800' },
              { label: 'Review → Merge', data: [1.5, 1.8, 1.6, 1.4, 1.3, 1.2], backgroundColor: '#2196F3' },
            ],
          },
          options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], stacked: true, ticks: { callback: v => v + 'h' } }], xAxes: [{ ...commonOptions.scales.xAxes[0], stacked: true }] } },
        },
        'github-top-reviewers': {
          type: 'bar',
          data: {
            labels: ['Ana S.', 'Carlos L.', 'Maria S.', 'Pedro C.', 'Lucas R.'],
            datasets: [{ label: 'Reviews', data: [42, 35, 28, 21, 18], backgroundColor: '#9C27B0', borderRadius: 4 }],
          },
          options: commonOptions,
        },
        'github-distribuicao-comentarios': {
          type: 'bar',
          data: {
            labels: weeks,
            datasets: [{ label: 'Comentários', data: [45, 52, 38, 61, 48, 55], backgroundColor: '#00BCD4', borderRadius: 4 }],
          },
          options: commonOptions,
        },
        'github-taxa-aprovacao': {
          type: 'line',
          data: {
            labels: weeks,
            datasets: [{ label: 'Aprovação (%)', data: [72, 75, 78, 80, 82, 85], borderColor: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.1)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#4CAF50' }],
          },
          options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { min: 0, max: 100, callback: v => v + '%' } }] } },
        },
        'github-commits-autor': {
          type: 'bar',
          data: {
            labels: ['Ana S.', 'Carlos L.', 'Maria S.', 'Pedro C.', 'Lucas R.'],
            datasets: [{ label: 'Commits', data: [68, 54, 47, 39, 32], backgroundColor: '#22d3ee', borderRadius: 4 }],
          },
          options: commonOptions,
        },
        'github-issues-evolucao': {
          type: 'bar',
          data: {
            labels: weeks,
            datasets: [
              { label: 'Abertas', data: [12, 15, 10, 18, 14, 11], backgroundColor: '#F44336', borderRadius: 4 },
              { label: 'Fechadas', data: [8, 12, 14, 10, 16, 13], backgroundColor: '#4CAF50', borderRadius: 4 },
            ],
          },
          options: commonOptions,
        },
        'github-hotfix-rate': {
          type: 'line',
          data: {
            labels: weeks,
            datasets: [{ label: 'Hotfixes (%)', data: [18, 15, 12, 14, 10, 8], borderColor: '#F44336', backgroundColor: 'rgba(244,67,54,0.1)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#F44336' }],
          },
          options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + '%' } }] } },
        },
        'github-branch-lifetime': {
          type: 'line',
          data: {
            labels: weeks,
            datasets: [{ label: 'Dias médios', data: [3.5, 3.2, 2.9, 3.1, 2.7, 2.5], borderColor: '#607D8B', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 4, pointBackgroundColor: '#607D8B' }],
          },
          options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + 'd' } }] } },
        },
        'jira-task-cycle-time': {
          type: 'line',
          data: {
            labels: weeks,
            datasets: [
              { label: 'Projeto Alpha', data: [4.0, 3.8, 3.5, 3.2, 3.0, 2.8], borderColor: '#FF5722', backgroundColor: 'rgba(255,87,34,0.08)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#FF5722' },
              { label: 'Projeto Beta', data: [3.5, 3.3, 3.0, 2.8, 2.5, 2.3], borderColor: '#2196F3', backgroundColor: 'rgba(33,150,243,0.08)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#2196F3' },
              { label: 'Projeto Gamma', data: [2.5, 2.3, 2.2, 2.0, 1.8, 1.6], borderColor: '#FF9800', backgroundColor: 'rgba(255,152,0,0.08)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#FF9800' },
            ],
          },
          options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + 'd' } }] } },
        },
        'jira-lead-time': {
          type: 'line',
          data: {
            labels: weeks,
            datasets: [{ label: 'Lead Time (dias)', data: [8, 7.5, 7, 6.5, 6, 5.5], borderColor: '#9C27B0', backgroundColor: 'rgba(156,39,176,0.08)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#9C27B0' }],
          },
          options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + 'd' } }] } },
        },
        'jira-burndown': {
          type: 'line',
          data: {
            labels: ['Dia 1', 'Dia 3', 'Dia 5', 'Dia 7', 'Dia 9', 'Dia 11'],
            datasets: [
              { label: 'Ideal', data: [100, 83, 66, 50, 33, 0], borderColor: '#2196F3', backgroundColor: 'transparent', borderWidth: 2, borderDash: [5, 5], pointRadius: 0 },
              { label: 'Real', data: [100, 88, 72, 58, 40, 12], borderColor: '#22d3ee', backgroundColor: 'rgba(34,211,238,0.08)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#22d3ee' },
            ],
          },
          options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + '%' } }] } },
        },
        'jira-bugs-rate': {
          type: 'bar',
          data: {
            labels: ['Projeto Alpha', 'Projeto Beta', 'Projeto Gamma'],
            datasets: [{ label: 'Bugs (%)', data: [12, 8, 5], backgroundColor: '#F44336', borderRadius: 4 }],
          },
          options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + '%' } }] } },
        },
        'jira-throughput': {
          type: 'bar',
          data: {
            labels: weeks,
            datasets: [{ label: 'Tasks concluídas', data: [18, 22, 19, 25, 21, 24], backgroundColor: '#4CAF50', borderRadius: 4 }],
          },
          options: commonOptions,
        },
        'azure-build-success': {
          type: 'line',
          data: {
            labels: weeks,
            datasets: [{ label: 'Sucesso (%)', data: [88, 91, 89, 93, 95, 94], borderColor: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.1)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#4CAF50' }],
          },
          options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { min: 0, max: 100, callback: v => v + '%' } }] } },
        },
        'azure-deploy-frequency': {
          type: 'bar',
          data: {
            labels: weeks,
            datasets: [{ label: 'Deploys', data: [4, 6, 5, 8, 7, 9], backgroundColor: '#2196F3', borderRadius: 4 }],
          },
          options: commonOptions,
        },
        'azure-pipeline-duration': {
          type: 'line',
          data: {
            labels: weeks,
            datasets: [{ label: 'Duração (min)', data: [12, 11, 13, 10, 9, 8], borderColor: '#FF9800', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 4, pointBackgroundColor: '#FF9800' }],
          },
          options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + 'min' } }] } },
        },
        'sonar-code-coverage': {
          type: 'line',
          data: {
            labels: weeks,
            datasets: [{ label: 'Cobertura (%)', data: [62, 65, 68, 70, 72, 75], borderColor: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.1)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#4CAF50' }],
          },
          options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { min: 0, max: 100, callback: v => v + '%' } }] } },
        },
        'sonar-code-smells': {
          type: 'bar',
          data: {
            labels: weeks,
            datasets: [{ label: 'Code Smells', data: [120, 105, 98, 88, 80, 72], backgroundColor: '#FF9800', borderRadius: 4 }],
          },
          options: commonOptions,
        },
        'sonar-technical-debt': {
          type: 'line',
          data: {
            labels: weeks,
            datasets: [{ label: 'Horas estimadas', data: [48, 42, 38, 34, 30, 26], borderColor: '#9C27B0', backgroundColor: 'rgba(156,39,176,0.08)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#9C27B0' }],
          },
          options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + 'h' } }] } },
        },
      }

      return configs[metric.id] || null
    },

    // Inicializa todos os gráficos Chart.js das métricas favoritas
    initFavoriteCharts() {
      this.$nextTick(() => {
        this.favoriteMetrics.forEach(metric => {
          if (metric.chartType === 'velocity-table') return

          const refName = 'favoriteChart_' + metric.id
          const refEl = this.$refs[refName]
          const canvas = Array.isArray(refEl) ? refEl[0] : refEl
          if (!canvas) return

          // Usar dados reais do back se disponíveis, senão fallback para mock
          const apiData = this.favoriteMetricsData[metric.id]
          const config = apiData
            ? this.buildChartConfig(metric, apiData)
            : this.getMockChartConfig(metric)
          if (!config) return

          // Destruir instância anterior se existir
          if (this.favoriteChartInstances[metric.id]) {
            this.favoriteChartInstances[metric.id].destroy()
          }

          const ctx = canvas.getContext('2d')
          this.$set(this.favoriteChartInstances, metric.id, new Chart(ctx, config))
        })
      })
    },
  },
}
</script>

<style scoped>
/* Fundo Global Premium */
.dm-wrappe {
  background-color: var(--bg-main, #0D0D0D) !important;
  max-width: 1440px;
  margin: 0 auto;
}
/* =========================================================================
   SISTEMA DE VARIÁVEIS RESPONSIVAS (DARK / LIGHT MODE)
========================================================================= */
.theme--dark {
  --dm-bg-base: #0A0A0B;
  --dm-card-bg: #111113;
  --dm-border-color: rgba(255, 255, 255, 0.07);
  --dm-text-main: #FFFFFF;
  --dm-text-muted: rgba(255, 255, 255, 0.55);
  --dm-hover-bg: rgba(255, 255, 255, 0.04);
  --dm-btn-secondary-border: rgba(255, 255, 255, 0.2);
  --dm-btn-secondary-hover: rgba(255, 255, 255, 0.1);
}

.theme--light {
  --dm-bg-base: #F5F7FA;
  --dm-card-bg: #FFFFFF;
  --dm-border-color: rgba(0, 0, 0, 0.08);
  --dm-text-main: #1D1D1F;
  --dm-text-muted: rgba(0, 0, 0, 0.55);
  --dm-hover-bg: rgba(0, 0, 0, 0.02);
  --dm-btn-secondary-border: rgba(0, 0, 0, 0.2);
  --dm-btn-secondary-hover: rgba(0, 0, 0, 0.05);
}

/* ==================== ESTRUTURA GERAL ==================== */

.dm-text-main { color: var(--dm-text-main) !important; }
.dm-text-muted { color: var(--dm-text-muted) !important; }
.dm-border-color { border-color: var(--dm-border-color) !important; }
.border-bottom { border-bottom: 1px solid var(--dm-border-color); }
.border-top { border-top: 1px solid var(--dm-border-color); }

/* ==================== COMPONENTES BASE ==================== */
.dm-card {
  background-color: var(--dm-card-bg) !important;
  border: 1px solid var(--dm-border-color) !important;
  border-radius: 16px !important;
  transition: all 0.2s ease;
}

.dm-hover-card:hover, .stat-card:hover {
  transform: translateY(-4px);
  border-color: rgba(34, 211, 238, 0.3) !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
}

.dm-icon-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
}
.dm-icon-badge.small { width: 32px; height: 32px; border-radius: 8px; }
.dm-icon-badge.large { width: 56px; height: 56px; border-radius: 16px; }
.dm-icon-badge.mini { width: 28px; height: 28px; border-radius: 6px; }

/* Utilitários de Cor de Fundo Suave */
.bg-cyan { background-color: rgba(34, 211, 238, 0.12) !important; }
.bg-orange { background-color: rgba(255, 152, 0, 0.12) !important; }
.bg-red { background-color: rgba(244, 67, 54, 0.12) !important; }
.bg-blue { background-color: rgba(33, 150, 243, 0.12) !important; }
.bg-green { background-color: rgba(76, 175, 80, 0.12) !important; }
.bg-purple { background-color: rgba(156, 39, 176, 0.12) !important; }
.bg-primary { background-color: rgba(34, 211, 238, 0.08) !important; border: 1px solid rgba(34, 211, 238, 0.2); }

.bg-cyan-tint { background-color: rgba(34, 211, 238, 0.03) !important; }
.bg-orange-tint { background-color: rgba(255, 152, 0, 0.03) !important; }

/* ==================== TIPOGRAFIA E HEADER ==================== */
.tracking-wider { letter-spacing: 0.05em; }

.dm-header-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(34, 211, 238, 0.1);
  border: 1px solid rgba(34, 211, 238, 0.2);
}

.dm-header-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--dm-text-main);
}

.dm-header-subtitle {
  font-size: 14px;
  color: var(--dm-text-muted);
  margin-top: 2px;
}

/* ==================== FILTROS E INPUTS ==================== */
::v-deep .dm-input .v-input__slot {
  background-color: var(--dm-card-bg) !important;
  border-color: var(--dm-border-color) !important;
  color: var(--dm-text-main) !important;
}
::v-deep .dm-input .v-text-field__details { display: none !important; }
::v-deep .dm-input input, ::v-deep .dm-input .v-select__selection {
  color: var(--dm-text-main) !important;
}

/* Botões */
.dm-btn-primary {
  background: #22d3ee !important; /* Cor neon do Design System */
  color: #0A0A0B !important;
  letter-spacing: 0;
  text-transform: none;
}
.dm-btn-primary:hover { opacity: 0.9; }

.dm-btn-secondary {
  border-color: var(--dm-btn-secondary-border) !important;
  color: var(--dm-text-main) !important;
  letter-spacing: 0;
  text-transform: none;
}
.dm-btn-secondary:hover { background-color: var(--dm-btn-secondary-hover) !important; }

.dm-btn-text {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
}

/* Toggle Group */
::v-deep .dm-toggle-group {
  background-color: var(--dm-card-bg) !important;
  border: 1px solid var(--dm-border-color) !important;
}
::v-deep .dm-toggle-group .v-btn {
  color: var(--dm-text-muted) !important;
  background-color: transparent !important;
  border: none !important;
}
::v-deep .dm-toggle-group .v-btn--active {
  background-color: #22d3ee !important;
  color: #0A0A0B !important;
}
::v-deep .dm-toggle-group .v-btn--active .v-icon { color: #0A0A0B !important; }

::v-deep .dm-toggle-group-small {
  background-color: var(--dm-bg-base) !important;
  border: 1px solid var(--dm-border-color) !important;
}
::v-deep .dm-toggle-group-small .v-btn { border: none !important; }
::v-deep .dm-toggle-group-small .v-btn--active {
  background-color: rgba(34, 211, 238, 0.15) !important;
  color: #22d3ee !important;
}

/* Chips */
::v-deep .dm-chip {
  background-color: var(--dm-hover-bg) !important;
  color: var(--dm-text-main) !important;
  border: 1px solid var(--dm-border-color) !important;
}
::v-deep .dm-chip-active {
  background-color: #22d3ee !important;
  color: #0A0A0B !important;
  border-color: #22d3ee !important;
}

/* ==================== EXPANSION PANELS (ACORDEÃO) ==================== */
::v-deep .dm-expansion-panels .v-expansion-panel {
  background-color: transparent !important;
  border: none !important;
  border-bottom: 1px solid var(--dm-border-color) !important;
}
::v-deep .dm-expansion-panels .v-expansion-panel::before { box-shadow: none !important; }
::v-deep .dm-expansion-panels .v-expansion-panel-header {
  padding: 16px 20px;
}
::v-deep .dm-expansion-panels .v-expansion-panel-header__icon .v-icon { color: var(--dm-text-muted) !important; }

/* ==================== TABELAS E LISTAS ==================== */
::v-deep .dm-table {
  background-color: transparent !important;
}
::v-deep .dm-table th {
  color: var(--dm-text-muted) !important;
  border-bottom: 1px solid var(--dm-border-color) !important;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.05em;
}
::v-deep .dm-table td {
  border-bottom: 1px solid var(--dm-border-color) !important;
}
::v-deep .dm-table tbody tr:hover {
  background-color: var(--dm-hover-bg) !important;
}

/* ==================== SEÇÃO: JIRA METRICS ==================== */
.dm-metric-box {
  background-color: var(--dm-bg-base);
  border: 1px solid var(--dm-border-color);
  border-radius: 12px;
  padding: 16px;
  height: 100%;
}

.dm-user-card {
  background-color: var(--dm-bg-base);
  border: 1px solid var(--dm-border-color);
  transition: all 0.2s;
}
.dm-user-card:hover {
  border-color: rgba(34,211,238,0.3);
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}

.dm-stat-mini {
  background-color: var(--dm-card-bg);
  border: 1px solid var(--dm-border-color);
  border-radius: 8px;
  padding: 8px 4px;
  flex: 1;
  margin: 0 4px;
}
.dm-stat-mini:first-child { margin-left: 0; }
.dm-stat-mini:last-child { margin-right: 0; }

.dm-medal {
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
  border: 2px solid var(--dm-card-bg);
  color: #0A0A0B;
}
.dm-medal.small { width: 18px; height: 18px; font-size: 9px; bottom: 0; right: -4px; }
.medal-1 { background-color: #FFC107; }
.medal-2 { background-color: #E0E0E0; color: #424242; }
.medal-3 { background-color: #FF9800; }

.dm-adv-metric-box {
  background-color: var(--dm-bg-base);
  border: 1px solid var(--dm-border-color);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

/* ==================== SEÇÃO: IA INSIGHTS ==================== */
.ai-glow-card {
  border: 1px solid rgba(34, 211, 238, 0.2) !important;
  background: linear-gradient(180deg, var(--dm-card-bg) 0%, var(--dm-bg-base) 100%) !important;
  box-shadow: 0 0 20px rgba(34, 211, 238, 0.05) !important;
}

.dm-ai-insight-box {
  position: relative;
  background-color: var(--dm-bg-base);
  border: 1px solid var(--dm-border-color);
  border-radius: 12px;
  display: flex;
  overflow: hidden;
}

.dm-ai-insight-border {
  width: 4px;
  flex-shrink: 0;
}

.dm-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--dm-border-color);
  cursor: pointer;
  transition: all 0.3s ease;
}
.dm-dot.active {
  transform: scale(1.3);
}

.dm-btn-action {
  border-color: var(--dm-border-color) !important;
  color: var(--dm-text-main) !important;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
}
.dm-btn-action:hover {
  background-color: var(--dm-hover-bg) !important;
  border-color: #22d3ee !important;
  color: #22d3ee !important;
}

/* ==================== SEÇÃO: ENGENHARIA ==================== */
.dm-hero-gradient {
  background: linear-gradient(135deg, rgba(34, 211, 238, 0.15) 0%, rgba(156, 39, 176, 0.15) 100%);
  border-bottom: 1px solid var(--dm-border-color);
}
.profile-avatar {
  border: 2px solid rgba(255,255,255,0.2);
  box-shadow: 0 0 20px rgba(34,211,238,0.2);
}

.bg-ai-bar {
  background-color: var(--dm-hover-bg);
  border-bottom: 1px solid var(--dm-border-color);
}

.dm-legend-dot {
  width: 12px; height: 12px; border-radius: 50%;
}
.dm-legend-dot.cyan { background-color: #22d3ee; }
.dm-legend-dot.dashed { border: 2px dashed #BDBDBD; background: transparent; }

.dm-dimension-row {
  padding: 8px;
  border-radius: 8px;
  transition: background 0.2s ease;
  cursor: pointer;
}
.dm-dimension-row:hover { background-color: var(--dm-hover-bg); }

/* SCROLLBAR CUSTOMIZADA */
.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: var(--dm-border-color); border-radius: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(136, 136, 136, 0.5); }

/* ==================== DRAWER CATÁLOGO ==================== */
::v-deep .dm-drawer {
  background-color: var(--dm-card-bg) !important;
  border-left: 1px solid var(--dm-border-color) !important;
}
.bg-drawer-filters { background-color: var(--dm-bg-base); }
.dm-icon-btn { color: var(--dm-text-muted) !important; }
.dm-icon-btn:hover { color: var(--dm-text-main) !important; }
.dm-icon-btn.active { color: #22d3ee !important; }

/* FIX DATEPICKER NO DARK MODE */
::v-deep .v-date-picker-header .v-btn,
::v-deep .v-date-picker-header .v-btn .v-icon,
::v-deep .v-date-picker-header__value button {
  color: var(--dm-text-main) !important;
}
::v-deep .theme--dark.v-picker__body { background-color: var(--dm-card-bg) !important; }

</style>

<style>
/* Estilos globais (sem scoped) para v-tooltip renderizado fora do componente */
.dm-tooltip {
  background: #111113 !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  color: #FFFFFF !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5) !important;
  font-family: 'Inter', sans-serif !important;
}
</style>